import React, { useState, useEffect } from 'react'
import { setUrlParams } from '../../utils/urlState.js'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { downloadProjectExport, exportMessage } from '../../utils/projectExport'
import { useAuth, userRoles } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects'
import api from '../../services/api'
import { formatDate, formatDateTime } from '../../utils/helpers'
import ProjectLifecycleMap from './ProjectLifecycleMap.jsx'
import DeliverablesTab from './DeliverablesTab.jsx'
import DocumentsTab from './DocumentsTab.jsx'
import AIHealthBadge from '../ai/AIHealthBadge.jsx'
import MilestoneChat from '../chat/MilestoneChat.jsx'
import { useTranslation } from 'react-i18next'

function ProjectDetails() {
  const { t } = useTranslation('projectDetails')
  const { t: tc } = useTranslation('common')
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('overview')
  // Deep links from notifications:
  //   ?milestone=<id>  → open Deliverables + auto-expand that milestone's chat (existing)
  //   ?tab=X           → activate a tab (Mam's logical names map onto the real tabs;
  //                      milestones & invoices both live under Deliverables)
  //   ?highlight=<id>  → scroll to + briefly pulse that milestone/deliverable
  const [searchParams] = useSearchParams()
  const deepLinkMilestoneId = searchParams.get('milestone')
  const tabParam = searchParams.get('tab')
  const highlightId = searchParams.get('highlight') || deepLinkMilestoneId
  const dossierParam = searchParams.get('dossier')
  // ?confirmInvoice=<id> → after a consultant updates banking from the confirm-invoice dialog,
  // ProfileSettings returns here and we re-open that milestone's Confirm & Sign Invoice dialog.
  const autoConfirmMilestoneId = searchParams.get('confirmInvoice')
  useEffect(() => {
    const aliases = { milestones: 'deliverables', invoices: 'deliverables', deliverables: 'deliverables', documents: 'documents', budget: 'budget', team: 'team', history: 'history', overview: 'overview' }
    if (deepLinkMilestoneId || autoConfirmMilestoneId) { setActiveTab('deliverables'); return }
    if (tabParam && aliases[tabParam]) setActiveTab(aliases[tabParam])
  }, [deepLinkMilestoneId, tabParam, autoConfirmMilestoneId])
  const [generatingContract, setGeneratingContract] = useState(false)
  const [projectContract, setProjectContract] = useState(null) // existing contract for this project if any
  const { getProjectById, updateProject, refresh, loading: projectsLoading,
          getSubprojects, getProjectRollup, promoteMilestoneLead } = useProjects()
  // v3.2 nested projects
  const [subprojects, setSubprojects] = useState([])
  const [rollup, setRollup] = useState(null)
  const [promoteBusyId, setPromoteBusyId] = useState(null)
  const [tasks, setTasks] = useState([])
  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [assignLoading, setAssignLoading] = useState(false)
  const [newConsultantId, setNewConsultantId] = useState('')
  const [consultantFilter, setConsultantFilter] = useState('')   // filter the offer picker by name/specialization/country/experience
  // Which open milestones the PM has ticked to offer this consultant. Empty = offer all
  // open ones (backend default). Lets the PM give DIFFERENT milestones to each consultant.
  const [offerMilestoneIds, setOfferMilestoneIds] = useState([])
  const [consultants, setConsultants] = useState([])   // all Active consultants for the dropdown
  const [pmUsers, setPmUsers] = useState([])           // Project Managers for the assign dropdown
  const [financeUsers, setFinanceUsers] = useState([]) // Finance users for the assign dropdown
  const [assignRoleBusy, setAssignRoleBusy] = useState(false)
  const [consultantSearch, setConsultantSearch] = useState('')
  const [showConsultantDropdown, setShowConsultantDropdown] = useState(false)

  // Modal states for Phase A2 + A3 fixes
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddTaskModal, setShowAddTaskModal] = useState(false)
  const [editForm, setEditForm] = useState(null)
  const [editSaving, setEditSaving] = useState(false)
  const [taskForm, setTaskForm] = useState({ title: '', description: '', status: 'Not Started', priority: 'Medium', due_date: '', budget: '' })
  const [taskSaving, setTaskSaving] = useState(false)
  const [feedback, setFeedback] = useState(null)

  // A project opened from a notification/deep-link may not be in the role-filtered context
  // list yet (or ever, for edge roles). Fall back to fetching it directly so we don't flash
  // "Not Found" on a project that genuinely exists and the user can access.
  const [fetchedProject, setFetchedProject] = useState(null)
  const [projectNotFound, setProjectNotFound] = useState(false)
  const project = getProjectById(parseInt(id)) || fetchedProject
  useEffect(() => {
    if (!id) return
    if (getProjectById(parseInt(id))) { setProjectNotFound(false); return }  // already in context
    let cancelled = false
    setProjectNotFound(false)
    api.get(`/projects/${id}`)
      .then(r => { if (!cancelled) setFetchedProject(r.data) })
      .catch(e => { if (!cancelled && e.response?.status === 404) setProjectNotFound(true) })
    return () => { cancelled = true }
  }, [id])

  // Extra project data fetched directly (team names, milestones)
  const [milestones, setMilestones] = useState([])
  const [teamNames, setTeamNames] = useState({ pm: null, consultant: null, funder: null })
  // v2.2 scope-acceptance flow
  const [selectedScopeIds, setSelectedScopeIds] = useState([])
  const [scopeSubmitting, setScopeSubmitting] = useState(false)
  const [approvingFor, setApprovingFor] = useState(null) // consultant_id being approved
  const [team, setTeam] = useState(null)
  const [deliverablesDot, setDeliverablesDot] = useState(null) // dot for the Deliverables tab
  const [retention, setRetention] = useState(null) // archiving/retention snapshot
  const [purging, setPurging] = useState(false)

  const reloadTasks = async () => {
    try {
      const tasksRes = await api.get(`/projects/${id}/tasks/`)
      setTasks(tasksRes.data)
    } catch (e) { console.error(e) }
  }

  const reloadHistory = async () => {
    try {
      setHistoryLoading(true)
      const historyRes = await api.get(`/projects/${id}/history/`)
      setHistory(historyRes.data)
    } catch (e) { console.error(e) } finally { setHistoryLoading(false) }
  }

  const reloadMilestones = async () => {
    try {
      const r = await api.get(`/projects/${id}/milestones/`)
      setMilestones(r.data || [])
    } catch (e) { console.error(e) }
  }

  // Deliverable/invoice actions (approve, confirm invoice, management-approve, payment)
  // change project.progress/spent_budget/remaining_budget/status — none of that is reflected
  // here until something re-fetches. `refresh()` from useProjects() updates `project` itself;
  // reloadMilestones() updates this page's own milestones state; lifecycleRefreshKey forces
  // ProjectLifecycleMap to re-fetch its own independent milestones/contracts snapshot.
  const [lifecycleRefreshKey, setLifecycleRefreshKey] = useState(0)
  const handleProjectDataChange = () => {
    refresh()
    reloadMilestones()
    setLifecycleRefreshKey(k => k + 1)
  }

  // Compute the Deliverables tab dot based on current role + deliverable/milestone state
  const reloadDeliverablesDot = async () => {
    try {
      const dl = await api.get(`/deliverables/project/${id}`)
      const deliverables = dl.data || []
      const ms = await api.get(`/projects/${id}/milestones/`)
      const msList = ms.data || []

      const isConsultantRole = user?.role === 'Consultant'
      const isPMAdminRole = user?.role === 'Project Manager' || user?.role === 'Admin'

      if (isConsultantRole) {
        if (deliverables.some(d => d.status === 'Revision Requested'))
          return setDeliverablesDot({ color: 'bg-red-500', pulse: true, label: 'Revision needed' })
        if (msList.some(m => m.invoice_triggered && !m.consultant_invoice_confirmed))
          return setDeliverablesDot({ color: 'bg-amber-400', pulse: true, label: 'Confirm invoice' })
        if (msList.some(m => m.consultant_invoice_confirmed && !(m.admin_invoice_approved && m.pm_invoice_approved)))
          return setDeliverablesDot({ color: 'bg-blue-400', pulse: false, label: 'Awaiting approval' })
      }
      if (isPMAdminRole) {
        if (deliverables.some(d => ['Submitted', 'Revision Requested'].includes(d.status)))
          return setDeliverablesDot({ color: 'bg-amber-400', pulse: true, label: 'Review needed' })
        const allApproved = msList.some(m => {
          const items = deliverables.filter(d => d.milestone_id === m.id)
          return items.length > 0 && items.every(d => d.status === 'Approved') && !m.invoice_triggered
        })
        if (allApproved)
          return setDeliverablesDot({ color: 'bg-blue-400', pulse: true, label: 'Generate invoice' })
        const myApproval = msList.some(m => m.consultant_invoice_confirmed && !(user?.role === 'Admin' ? m.admin_invoice_approved : m.pm_invoice_approved))
        if (myApproval)
          return setDeliverablesDot({ color: 'bg-amber-400', pulse: true, label: 'Approve invoice' })
      }
      setDeliverablesDot(null)
    } catch { setDeliverablesDot(null) }
  }

  // Look up real names for PM / Consultant / Funder IDs
  const loadTeamNames = async (p) => {
    if (!p) return
    try {
      const usersRes = await api.get('/users/')
      const users = usersRes.data || []
      const find = (uid) => users.find(u => u.id === uid)?.full_name || null
      setTeamNames({
        pm: find(p.project_manager_id || p.projectManagerId),
        consultant: find(p.consultant_id || p.consultantId),
        funder: find(p.funder_id || p.funderId),
      })
    } catch (e) { /* non-fatal */ }
  }

  // Fetch the contract for this project (latest one)
  const reloadContract = async () => {
    try {
      const r = await api.get('/contracts/')
      const found = (r.data || [])
        .filter(c => c.project_id === parseInt(id))
        .sort((a, b) => b.id - a.id)[0] || null
      setProjectContract(found)
    } catch { setProjectContract(null) }
  }

  // Retention snapshot (clocks + per-type counts) for the archived banner / retention card.
  const reloadRetention = async () => {
    try {
      const r = await api.get(`/closure/retention/${id}`)
      setRetention(r.data)
    } catch { setRetention(null) }
  }

  const purgeArtifacts = async (artifactType) => {
    const label = { deliverables: 'deliverables', documents: 'documents', financial: 'money records (contracts, invoices and bills)' }[artifactType]
    if (!window.confirm(`Permanently delete the files of all EXPIRED ${label}? The records are kept for audit. This cannot be undone.`)) return
    setPurging(true)
    try {
      const r = await api.post(`/closure/retention/${id}/purge?artifact_type=${artifactType}`)
      setFeedback({ type: 'success', text: r.data?.detail || 'Purge complete.' })
      await reloadRetention()
    } catch (e) {
      setFeedback({ type: 'error', text: e?.response?.data?.detail || 'Purge failed.' })
    } finally {
      setPurging(false)
    }
  }

  // Integrity: fingerprint the files, or check them against the fingerprints taken at archive time.
  const [fixityBusy, setFixityBusy] = useState(false)
  const runFixity = async (action) => {
    setFixityBusy(true)
    try {
      const r = await api.post(`/closure/${id}/fixity/${action}`)
      setFeedback({ type: r.data?.ok === false ? 'error' : 'success', text: r.data?.detail || 'Done.' })
      await reloadRetention()
    } catch (e) {
      setFeedback({ type: 'error', text: e?.response?.data?.detail || 'Could not do that.' })
    } finally {
      setFixityBusy(false)
    }
  }

  // The permanent list of what was destroyed, as a spreadsheet, for an auditor or a person who asked.
  const downloadDisposal = async () => {
    try {
      const r = await api.get(`/closure/disposal/${id}?format=csv`, { responseType: 'blob' })
      const match = /filename="?([^";]+)"?/.exec(r.headers?.['content-disposition'] || '')
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url
      a.download = match ? match[1] : `project-${id}-disposal-record.csv`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setFeedback({ type: 'error', text: 'Could not download the disposal record.' })
    }
  }

  // Admin: cancel (put on hold) a project with a reason, or reopen a cancelled one. Reversible.
  const cancelProject = async () => {
    const reason = window.prompt('Cancel this project (put it on hold)?\n\nOptionally add a reason — the PM and consultant will be notified. You can reopen it any time.')
    if (reason === null) return  // cancelled the prompt
    try {
      await api.post(`/projects/${id}/cancel/`, { reason })
      setFeedback({ type: 'success', message: 'Project cancelled. It is read-only until you reopen it.' })
      refresh?.()
    } catch (e) {
      setFeedback({ type: 'error', message: e?.response?.data?.detail || 'Could not cancel the project.' })
    }
  }
  const reopenProject = async () => {
    if (!window.confirm(t('dlg_reopen'))) return
    try {
      await api.post(`/projects/${id}/reopen/`)
      setFeedback({ type: 'success', message: 'Project reopened — it is active again.' })
      refresh?.()
    } catch (e) {
      setFeedback({ type: 'error', message: e?.response?.data?.detail || 'Could not reopen the project.' })
    }
  }

  useEffect(() => {
    if (!id) return
    reloadTasks()
    reloadHistory()
    reloadMilestones()
    reloadDeliverablesDot()
    reloadContract()
    reloadRetention()
    // Members must load on EVERY tab (not just Team) because canEditProject depends on it —
    // a delegated co-manager (ProjectMember.can_manage) needs the manage UI to appear on the
    // Overview/Deliverables/etc. tabs too, not only when they happen to open the Team tab.
    api.get(`/projects/${id}/members`).then(r => setMembers(r.data?.members || [])).catch(() => {})
    // Consultants/PMs/Finance are needed on the Team tab (Offer form) AND the Overview tab
    // (the Add-Milestone modal's workshop attendee picker lives there), so load them for both
    // — otherwise the workshop picker shows "No active consultants available".
    if (activeTab === 'team' || activeTab === 'overview') {
      api.get('/users/').then(r => {
        const all = r.data || []
        setConsultants(all.filter(u => u.role === 'Consultant'))
        setPmUsers(all.filter(u => u.role === 'Project Manager'))
        setFinanceUsers(all.filter(u => u.role === 'Finance'))
      }).catch(() => {})
    }
    if (activeTab === 'team') {
      api.get(`/projects/${id}/team`).then(r => setTeam(r.data)).catch(() => {})
    }
    // v3.2 nested projects: on a TOP-LEVEL project, load its sub-projects + rolled-up totals.
    if (activeTab === 'overview') {
      getSubprojects(id).then(setSubprojects).catch(() => {})
      getProjectRollup(id).then(setRollup).catch(() => {})
    }
  }, [id, activeTab])

  // On a SUB-project, load the parent so the inline editor can cap the end date at the parent's
  // (the backend enforces this too — this just stops the sub-PM typing a date that will be
  // refused on save). Silent on failure: the cap is a convenience, not the guard.
  const [parentProject, setParentProject] = useState(null)
  useEffect(() => {
    const pid = project?.parentProjectId || project?.parent_project_id
    if (!pid) { setParentProject(null); return }
    api.get(`/projects/${pid}`).then(r => setParentProject(r.data)).catch(() => setParentProject(null))
  }, [project?.parentProjectId, project?.parent_project_id])

  const [members, setMembers] = useState([])
  // Managers beyond the owning PM — they live on the member rows, not on the project.
  const coManagers = (members || []).filter(m =>
    m.can_manage && m.user_id !== (project?.project_manager_id ?? project?.projectManagerId))
  // Assign a PM (co-manager) or Finance (view) to this project's team via the delegate endpoint.
  const assignRoleMember = async (userId, canManage, personName = 'this person', roleLabel = 'team member') => {
    if (!userId) { alert(t('dlg_select_first')); return }
    if (!window.confirm(`Add ${personName} to this project as ${roleLabel}?`)) return
    setAssignRoleBusy(true)
    try {
      await api.post(`/projects/${id}/delegate`, { user_id: parseInt(userId), can_manage: canManage })
      const r = await api.get(`/projects/${id}/members`)
      setMembers(r.data?.members || [])
      setFeedback({ type: 'success', text: `${personName} added to the project team.` })
      alert(`${personName} has been added to the project team.`)
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Could not add team member.' })
      alert(e.response?.data?.detail || 'Could not add team member.')
    } finally {
      setAssignRoleBusy(false)
    }
  }
  const toggleDelegate = async (userId, canManage) => {
    try {
      await api.post(`/projects/${id}/delegate`, { user_id: userId, can_manage: canManage })
      const r = await api.get(`/projects/${id}/members`)
      setMembers(r.data?.members || [])
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Could not update access' })
    }
  }

  // v3.5: Admin designates a scoped Approver (counter-sign contracts + final invoice approval
  // for this program and its sub-projects, except a project they manage themselves).
  const toggleApprover = async (userId, grant) => {
    try {
      await api.put(`/projects/${id}/members/${userId}/approver?grant=${grant}`)
      const r = await api.get(`/projects/${id}/members`)
      setMembers(r.data?.members || [])
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Could not update approver rights' })
    }
  }

  // Default the consultant's milestone selection to all milestones offered to them
  useEffect(() => {
    const offered = milestones
      .filter(m => m.scope_status === 'Offered' && m.consultant_id === user?.id)
      .map(m => m.id)
    setSelectedScopeIds(offered)
  }, [milestones, user?.id])

  useEffect(() => {
    if (project) loadTeamNames(project)
  }, [project?.id])

  const handleAssignConsultant = async (e) => {
    e.preventDefault()
    if (!newConsultantId) { setFeedback({ type: 'error', text: 'Please select a consultant first.' }); return }
    setAssignLoading(true)
    try {
      // If the PM ticked a specific subset, offer exactly those (each consultant can get
      // DIFFERENT milestones). If none ticked, the backend offers all still-open milestones.
      await api.put(`/projects/${id}/assign/`, null, {
        params: {
          consultant_id: parseInt(newConsultantId),
          ...(offerMilestoneIds.length ? { milestone_ids: offerMilestoneIds } : {}),
        },
        // repeat the query key per id (?milestone_ids=1&milestone_ids=2) for FastAPI List[int]
        paramsSerializer: { indexes: null },
      })
      const selected = consultants.find(c => c.id === parseInt(newConsultantId))
      updateProject(parseInt(id), {
        consultantId: parseInt(newConsultantId),
        consultant: selected?.full_name || `Consultant ${newConsultantId}`,
      })
      const countLabel = offerMilestoneIds.length ? `${offerMilestoneIds.length} milestone(s)` : 'the open milestones'
      setFeedback({ type: 'success', text: `Offered ${countLabel} to ${selected?.full_name || 'consultant'} — they'll confirm which to take on.` })
      setNewConsultantId('')
      setOfferMilestoneIds([])
      setConsultantSearch('')
      refresh?.()
      reloadMilestones()
      api.get(`/projects/${id}/team`).then(r => setTeam(r.data)).catch(() => {})
      // Reload team names after assignment
      if (project) loadTeamNames({ ...project, consultant_id: parseInt(newConsultantId) })
    } catch (e) {
      // The assign form is at the bottom of the Team tab, but the feedback banner renders at
      // the top of the page (off-screen). Also alert() so the reason is never missed — the
      // common cases are "consultant not ACTIVE yet" and "no open milestones left to offer".
      const msg = e.response?.data?.detail || e.message || 'Could not offer milestones.'
      setFeedback({ type: 'error', text: msg })
      alert(t('dlg_offer_failed') + msg)
    } finally {
      setAssignLoading(false)
    }
  }

  // ── v2.2 scope-acceptance handlers ─────────────────────────────────────────
  const toggleScope = (mid) => {
    setSelectedScopeIds(prev => prev.includes(mid) ? prev.filter(x => x !== mid) : [...prev, mid])
  }

  const submitMilestoneSelection = async () => {
    setScopeSubmitting(true)
    try {
      const r = await api.post(`/projects/${id}/milestones/select`, { accepted_milestone_ids: selectedScopeIds })
      setFeedback({ type: 'success', text: r.data.accepted > 0
        ? `Submitted ${r.data.accepted} milestone(s) for manager approval.`
        : 'You declined all offered milestones.' })
      reloadMilestones()
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Failed to submit selection' })
    } finally {
      setScopeSubmitting(false)
    }
  }

  // Per-milestone accept/reject. Rejecting auto-opens the milestone chat so the
  // consultant can add a note/attachment and the PM/Admin can convince them.
  const [respondingId, setRespondingId] = useState(null)
  const respondMilestone = async (milestoneId, action) => {
    // On reject, ask WHY — the reason is posted to the milestone chat so the PM/Admin see it.
    let reason = null
    if (action === 'reject') {
      reason = window.prompt('Why are you rejecting this milestone? Your reason is shared with the manager.')
      if (reason === null) return          // cancelled
      if (!reason.trim()) { setFeedback({ type: 'error', text: 'Please give a short reason for rejecting.' }); return }
    }
    setRespondingId(milestoneId)
    try {
      const fd = new FormData()
      fd.append('action', action)
      await api.post(`/projects/${id}/milestones/${milestoneId}/respond`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      // Post the reason into the milestone chat thread (visible to PM/Admin).
      if (reason && reason.trim()) {
        const cfd = new FormData()
        cfd.append('content', `Rejected this milestone — reason: ${reason.trim()}`)
        await api.post(`/chat/milestone/${milestoneId}`, cfd, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => {})
      }
      setFeedback({ type: 'success', text: action === 'accept'
        ? 'Milestone accepted — sent to the manager for approval.'
        : 'Milestone declined and your reason was shared with the manager.' })
      reloadMilestones()
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Could not submit your response' })
    } finally {
      setRespondingId(null)
    }
  }

  const approveSelectionFor = async (consultantId) => {
    setApprovingFor(consultantId)
    try {
      const r = await api.post(`/projects/${id}/milestones/approve-selection`, null, {
        params: { consultant_id: consultantId },
      })
      setFeedback({ type: 'success', text: `Approved ${r.data.approved} milestone(s). Contract ${r.data.contract_number || ''} generated — the consultant can now sign.` })
      reloadMilestones()
      refresh?.()
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Failed to approve selection' })
    } finally {
      setApprovingFor(null)
    }
  }

  // ── Edit Project ───────────────────────────────────────────────────────────
  const openEditModal = () => {
    setEditForm({
      name: project.name || '',
      description: project.description || '',
      priority: project.priority || 'Medium',
      status: project.status || 'Planning',
      category: project.category || '',
      risk_level: project.riskLevel || project.risk_level || 'Low',
      start_date: project.startDate ? String(project.startDate).split('T')[0] : '',
      end_date: project.endDate ? String(project.endDate).split('T')[0] : '',
      total_budget: project.totalBudget || project.total_budget || 0,
    })
    setShowEditModal(true)
  }

  const saveEdit = async (e) => {
    e.preventDefault()
    setEditSaving(true)
    try {
      const resp = await api.put(`/projects/${id}`, editForm)
      setFeedback({ type: 'success', text: 'Project updated.' })
      setShowEditModal(false)
      refresh?.()
      // Update local cache
      updateProject(parseInt(id), {
        name: resp.data.name, description: resp.data.description,
        status: resp.data.status, priority: resp.data.priority,
        category: resp.data.category, totalBudget: resp.data.total_budget,
      })
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Update failed' })
    } finally {
      setEditSaving(false)
    }
  }

  // ── Edit Milestone ─────────────────────────────────────────────────────────
  const [editingMilestone, setEditingMilestone] = useState(null) // milestone object or null
  const [msEditForm, setMsEditForm] = useState({ title: '', amount: '', due_date: '', brief_instructions: '', paymentMode: 'Fixed' })
  const [msEditSaving, setMsEditSaving] = useState(false)
  const [briefUploading, setBriefUploading] = useState(null) // milestone id being uploaded

  const openEditMilestone = (m) => {
    setEditingMilestone(m)
    setMsEditForm({
      title: m.title || '',
      amount: m.payment_amount || m.amount_released_on_completion || '',
      due_date: m.due_date || '',
      brief_instructions: m.brief_instructions || '',
      paymentMode: m.payment_mode || m.paymentMode || 'Fixed',
      tentativeBudget: m.tentative_budget ?? '',
    })
  }

  const saveMilestoneEdit = async (e) => {
    e.preventDefault()
    setMsEditSaving(true)
    try {
      const params = new URLSearchParams()
      if (msEditForm.title) params.append('title', msEditForm.title)
      if (msEditForm.amount !== '') params.append('payment_amount', msEditForm.amount)
      if (msEditForm.due_date) params.append('due_date', msEditForm.due_date)
      if (msEditForm.brief_instructions !== '') params.append('brief_instructions', msEditForm.brief_instructions)
      // Type is only user-chosen on a Hybrid project; send it so the milestone's mode persists.
      if ((project.budget_type || 'Fixed') === 'Hybrid') params.append('payment_mode', msEditForm.paymentMode)
      // Reimbursable milestones carry the PM/Admin-only System/Tentative Budget cap (0 clears it).
      const _bt = project.budget_type || project.budgetType || 'Fixed'
      const _isReimb = _bt === 'Actual' || (_bt === 'Hybrid' && msEditForm.paymentMode === 'Actual')
      if (_isReimb) params.append('tentative_budget', msEditForm.tentativeBudget === '' ? 0 : msEditForm.tentativeBudget)
      await api.put(`/projects/milestones/${editingMilestone.id}?${params.toString()}`)
      setFeedback({ type: 'success', text: 'Milestone updated.' })
      setEditingMilestone(null)
      reloadMilestones()
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Update failed' })
    } finally {
      setMsEditSaving(false)
    }
  }

  const deleteMilestone = async (m) => {
    if (!window.confirm(`Delete milestone "${m.title}"?\n\nThis cannot be undone. (A milestone that has deliverables or has been invoiced/paid can't be deleted.)`)) return
    try {
      await api.delete(`/projects/milestones/${m.id}`)
      setFeedback({ type: 'success', text: 'Milestone deleted.' })
      reloadMilestones()
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Delete failed' })
    }
  }

  const uploadBrief = async (milestoneId, file) => {
    if (!file) return
    setBriefUploading(milestoneId)
    try {
      const fd = new FormData()
      fd.append('file', file)
      await api.post(`/projects/milestones/${milestoneId}/brief`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setFeedback({ type: 'success', text: `Brief uploaded for milestone. Consultant will be notified.` })
      reloadMilestones()
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Upload failed' })
    } finally {
      setBriefUploading(null)
    }
  }

  const downloadBrief = async (milestoneId) => {
    try {
      const resp = await api.get(`/projects/milestones/${milestoneId}/brief/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Could not download brief.' })
    }
  }

  // ── Add Milestone (post-creation) ─────────────────────────────────────────
  const [showAddMilestoneModal, setShowAddMilestoneModal] = useState(false)
  // isWorkshop + attendeeIds: a workshop/meeting milestone is the SAME work for several
  // consultants — one copy is created per attendee (Mam: workshops share a milestone).
  // paymentMode: 'Fixed' | 'Actual' — only user-selectable on a Hybrid project (Fixed projects
  // are always Fixed; Actual projects are always Actuals) — mirrors the create-project wizard.
  // consultantId: who this milestone is for. Adding a milestone and deciding who does it is one
  // decision to a PM, and it was two screens — add it here, then find it in the list and offer it.
  const [milestoneForm, setMilestoneForm] = useState({ title: '', amount: '', due_date: '', briefFile: null, isWorkshop: false, attendeeIds: [], paymentMode: 'Fixed', consultantId: '' })
  const BLANK_MILESTONE = { title: '', amount: '', due_date: '', briefFile: null, isWorkshop: false, attendeeIds: [], paymentMode: 'Fixed', consultantId: '' }
  const [milestoneSaving, setMilestoneSaving] = useState(false)
  // Workshop attendee picker: dropdown open state + search filter.
  const [attendeeOpen, setAttendeeOpen] = useState(false)
  const [attendeeSearch, setAttendeeSearch] = useState('')

  // "Offer to more consultants" from an existing milestone card — reuses the workshop
  // endpoint to fan a FRESH copy (same title/amount/deadline/mode) out to more consultants.
  const [offerMoreFor, setOfferMoreFor] = useState(null)   // the source milestone, or null
  const [offerMoreIds, setOfferMoreIds] = useState([])
  const [offerMoreSearch, setOfferMoreSearch] = useState('')
  const [offerMoreOpen, setOfferMoreOpen] = useState(false)   // dropdown open/closed
  const [offerMoreSaving, setOfferMoreSaving] = useState(false)

  const submitOfferMore = async () => {
    if (!offerMoreFor || offerMoreIds.length === 0) return
    setOfferMoreSaving(true)
    try {
      const m = offerMoreFor
      const resp = await api.post(`/projects/${id}/milestones/workshop`, {
        title: m.title,
        due_date: (m.due_date || m.dueDate || '')?.slice(0, 10) || null,
        payment_amount: Number(m.payment_amount ?? m.paymentAmount ?? 0) || 0,
        payment_mode: (m.payment_mode || m.paymentMode || 'Fixed'),
        consultant_ids: offerMoreIds,
      })
      setFeedback({ type: 'success', text: resp?.data?.detail || `Offered "${m.title}" to ${offerMoreIds.length} more consultant(s).` })
      setOfferMoreFor(null); setOfferMoreIds([]); setOfferMoreSearch(''); setOfferMoreOpen(false)
      reloadMilestones()
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Failed to offer this milestone to more consultants' })
    } finally {
      setOfferMoreSaving(false)
    }
  }

  const submitMilestone = async (e) => {
    e.preventDefault()
    if (!milestoneForm.title.trim()) { setFeedback({ type: 'error', text: 'Milestone title is required' }); return }
    if (milestoneForm.isWorkshop && milestoneForm.attendeeIds.length === 0) {
      setFeedback({ type: 'error', text: 'Pick at least one consultant to attend this workshop/meeting.' }); return
    }
    setMilestoneSaving(true)
    try {
      // Resolve the milestone's payment mode from the project's budget type, mirroring the
      // create-project wizard: Fixed project → Fixed; Actual project → every milestone Actuals;
      // Hybrid project → the per-milestone choice. Actuals carries no fixed figure (claimed
      // with bills at invoice), so its amount is 0.
      const effectiveMode = _budgetType === 'Actual'
        ? 'Actual'
        : (_budgetType === 'Hybrid' ? milestoneForm.paymentMode : 'Fixed')
      const amount = effectiveMode === 'Actual' ? 0 : (milestoneForm.amount ? parseFloat(milestoneForm.amount) : 0)

      // Workshop/meeting: the SAME milestone is offered to several consultants — the backend
      // creates one copy per attendee (each owned normally so contracts/invoices/reimbursements
      // stay per-person). No brief-file upload path here (each copy is independent).
      if (milestoneForm.isWorkshop) {
        const wResp = await api.post(`/projects/${id}/milestones/workshop`, {
          title: milestoneForm.title.trim(),
          due_date: milestoneForm.due_date || null,
          payment_amount: amount,
          payment_mode: effectiveMode,
          consultant_ids: milestoneForm.attendeeIds,
        })
        setFeedback({ type: 'success', text: wResp?.data?.detail || `Workshop milestone offered to ${milestoneForm.attendeeIds.length} consultant(s).` })
        setShowAddMilestoneModal(false)
        setMilestoneForm(BLANK_MILESTONE)
        reloadMilestones()
        return
      }

      // A NAMED CONSULTANT GOES DOWN THE SAME ROAD AS A WORKSHOP, with one attendee.
      //
      // That endpoint already refuses a consultant who has not finished onboarding, with a
      // message saying whose and why, sets the milestone to Offered, and notifies them. Creating
      // the milestone here and assigning it separately would duplicate all three, and the copy
      // would drift from the original the first time one of them changed.
      let newMilestoneId = null
      if (milestoneForm.consultantId) {
        const oResp = await api.post(`/projects/${id}/milestones/workshop`, {
          title: milestoneForm.title.trim(),
          due_date: milestoneForm.due_date || null,
          payment_amount: amount,
          payment_mode: effectiveMode,
          consultant_ids: [parseInt(milestoneForm.consultantId)],
        })
        newMilestoneId = (oResp?.data?.milestone_ids || [])[0] || null
      } else {
        const msResp = await api.post(`/projects/${id}/milestones/`, {
          title: milestoneForm.title.trim(),
          project_id: parseInt(id),
          payment_mode: effectiveMode,
          payment_amount: amount,
          amount_released_on_completion: amount,
          due_date: milestoneForm.due_date || null,
        })
        newMilestoneId = msResp?.data?.id || null
      }

      // Upload brief file if one was attached
      if (milestoneForm.briefFile && newMilestoneId) {
        try {
          const fd = new FormData()
          fd.append('file', milestoneForm.briefFile)
          await api.post(`/projects/milestones/${newMilestoneId}/brief`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        } catch { /* non-fatal */ }
      }
      setFeedback({
        type: 'success',
        text: milestoneForm.consultantId
          ? 'Milestone added and offered to the consultant.'
          : 'Milestone added successfully.',
      })
      setShowAddMilestoneModal(false)
      setMilestoneForm(BLANK_MILESTONE)
      reloadMilestones()
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Failed to add milestone' })
    } finally {
      setMilestoneSaving(false)
    }
  }

  // ── Add Task ───────────────────────────────────────────────────────────────
  const openAddTaskModal = () => {
    setTaskForm({ title: '', description: '', status: 'Not Started', priority: 'Medium', due_date: '', budget: '' })
    setShowAddTaskModal(true)
  }

  const submitTask = async (e) => {
    e.preventDefault()
    if (!taskForm.title.trim()) { setFeedback({ type: 'error', text: 'Task title is required' }); return }
    setTaskSaving(true)
    try {
      const payload = {
        title: taskForm.title,
        description: taskForm.description || null,
        status: taskForm.status,
        priority: taskForm.priority,
        due_date: taskForm.due_date || null,
        budget: taskForm.budget ? parseFloat(taskForm.budget) : 0,
      }
      await api.post(`/projects/${id}/tasks/`, payload)
      setFeedback({ type: 'success', text: 'Task created successfully.' })
      setShowAddTaskModal(false)
      await reloadTasks()
      await reloadHistory()
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Failed to create task' })
    } finally {
      setTaskSaving(false)
    }
  }

  // ── Export Report (CSV) ────────────────────────────────────────────────────
  // EVERY RECORD, NOT THE SUMMARY. exportReport above is a spreadsheet of the project's
  // headline figures; this is the whole thing — every contract, invoice, deliverable and
  // document — for handing to somebody who does not have ProTrack.
  //
  // Here rather than only on the closure screen, which is where it used to live. That screen is
  // linked from this page only while a project is In Progress, Pending Closure or Closed, so on
  // an ARCHIVED project there was no route to it at all — and archived is precisely the case
  // this exists for. The records outlive the work.
  const [recordsBusy, setRecordsBusy] = useState(false)
  const [recordsNote, setRecordsNote] = useState(null)
  const exportEverything = async () => {
    setRecordsBusy(true); setRecordsNote(null)
    try {
      setRecordsNote(exportMessage(await downloadProjectExport(id)))
    } catch (e) {
      setRecordsNote({ bad: true, text: e.message })
    } finally { setRecordsBusy(false) }
  }

  const exportReport = () => {
    if (!project) return
    const rows = [
      ['Field', 'Value'],
      ['Project Name', project.name],
      ['Description', project.description || ''],
      ['Status', project.status],
      ['Priority', project.priority || ''],
      ['Category', project.category || ''],
      ['Start Date', project.startDate || ''],
      ['End Date', project.endDate || ''],
      ['Total Budget', project.totalBudget || project.total_budget || 0],
      ['Approved Budget', project.approvedBudget || project.approved_budget || 0],
      ['Spent Budget', project.spentBudget || project.spent_budget || 0],
      ['Remaining Budget', project.remainingBudget || project.remaining_budget || 0],
      ['Progress', `${project.progress || 0}%`],
      ['Risk Level', project.riskLevel || project.risk_level || ''],
      ['Project Manager', project.projectManager || ''],
      ['Funder', project.funder || ''],
      ['Consultant', project.consultant || ''],
      [],
      ['Tasks'],
      ['Title', 'Status', 'Priority', 'Due Date', 'Budget'],
      ...tasks.map(t => [t.title, t.status, t.priority || '', t.due_date || '', t.budget || 0]),
    ]
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(project.name || 'project').replace(/[^a-z0-9]/gi, '_')}_report.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    setFeedback({ type: 'success', text: 'Report downloaded.' })
  }

  // ── Generate Contract (Admin) ────────────────────────────────────────────
  const generateContract = async () => {
    if (!project.consultantId) {
      setFeedback({ type: 'error', text: 'Assign a consultant first (Team tab → Assign Consultant), then generate the contract.' })
      setActiveTab('team')
      return
    }
    setGeneratingContract(true)
    setFeedback(null)
    try {
      const resp = await api.post(`/contracts/generate/${id}`, {})
      setFeedback({ type: 'success', text: `Contract ${resp.data.contract_number} created. Opening it…` })
      await reloadContract()
      setTimeout(() => navigate(`/contracts/${resp.data.contract_id}`), 800)
    } catch (e) {
      setFeedback({ type: 'error', text: e.response?.data?.detail || 'Failed to generate contract' })
    } finally {
      setGeneratingContract(false)
    }
  }

  // ── Contact (mailto) ───────────────────────────────────────────────────────
  const contactMember = (member) => {
    if (!member.email) {
      setFeedback({ type: 'error', text: `No email on file for ${member.name}` })
      return
    }
    window.location.href = `mailto:${member.email}?subject=Regarding project: ${encodeURIComponent(project.name)}`
  }

  if (!project) {
    // Still resolving (context loading or the direct fetch in flight) — show a spinner,
    // not a scary "Not Found", unless the fetch actually came back 404.
    if (!projectNotFound) {
      return (
        <div className="p-6">
          <div className="card p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        </div>
      )
    }
    return (
      <div className="p-6">
        <div className="card p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900">{t('not_found')}</h3>
          <p className="mt-2 text-gray-600">{t('not_found_detail')}</p>
          <Link to="/projects" className="btn-primary mt-4">{t('back_to_projects')}</Link>
        </div>
      </div>
    )
  }

  const projectTasks = tasks.map(t => ({
    ...t,
    progress: t.status === 'Completed' ? 100 : t.status === 'In Progress' ? 50 : 0
  }))

  const isFinance = user?.role === userRoles.FINANCE
  // A company without the project flow keeps its dossiers and documents, not deliverables or budget.
  const projectFlow = user?.modules?.projects !== false
  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    // Tasks tab hidden in v1 per Maha's instruction — paused for next version
    // { id: 'tasks', name: 'Tasks', icon: '...' },
    // Finance doesn't work deliverables/documents and reads budget from the Overview tab,
    // so those tabs are hidden for them (per Mam).
    ...(isFinance ? [] : [
      { id: 'deliverables', name: 'Deliverables', icon: 'M9 17v-2a4 4 0 014-4h4l-4-4m0 8l4-4' },
      { id: 'documents', name: 'Documents', icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
      { id: 'budget', name: 'Budget', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1' },
    ]),
    { id: 'team', name: 'Team', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    // Activity History is hidden for Finance (per Mam + Pratik) — not relevant to their view.
    ...(isFinance
      ? []
      : [{ id: 'history', name: 'Activity History', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' }]),
  ]

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Progress': return 'bg-blue-100 text-blue-800'
      case 'Completed': return 'bg-green-100 text-green-800'
      case 'Planning': return 'bg-yellow-100 text-yellow-800'
      case 'Pending Approval': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800'
      case 'Medium': return 'bg-yellow-100 text-yellow-800'
      case 'Low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // A delegated co-manager (ProjectMember.can_manage) can edit/run the project exactly like
  // its PM — the backend already grants them manage access, so the UI must show them the same
  // controls. Without this, "Grant manage" set the permission but the co-manager never saw the
  // buttons to use it.
  const _myId = user?.id ?? user?.user_id
  const isCoManager = (members || []).some(m => m.user_id === _myId && m.can_manage)
  // A scoped approver fills the ADMIN invoice slot. Needed by DeliverablesTab because the approve
  // UI was gated on Admin/PM/co-manager only — so a Consultant approver saw no approve button at
  // all, and granting them manage to make it appear routed their approval to the PM slot instead.
  const isApproverHere = (members || []).some(m => m.user_id === _myId && m.can_approve && !m.can_manage)
  // A Master/Default PM (User.is_primary_pm) manages every project, same as owning it.
  const isPrimaryPm = user?.role === userRoles.PROJECT_MANAGER && user?.is_primary_pm
  const canEditProject = user?.role === userRoles.ADMIN || isPrimaryPm ||
    (user?.role === userRoles.PROJECT_MANAGER && (project.projectManagerId === user?.id || project.projectManagerId === user?.user_id)) ||
    isCoManager

  // v3.2 nested projects / scoped roles
  const isSubProject = !!(project.parentProjectId || project.parent_project_id)
  const myMembership = (members || []).find(m => m.user_id === _myId)
  const isSubLeadHere = myMembership?.project_role === 'Sub-Lead'
  // Who may spin up a sub-project under THIS (top-level) program: its managers, or a consultant
  // the manager promoted to Sub-Lead. Never on a project that is itself already a sub-project.
  const canCreateSubproject = !isSubProject && (canEditProject || isSubLeadHere)

  const goCreateSubproject = () => navigate(`/projects?subOf=${project.id}`)
  const handlePromoteLead = async (m) => {
    const cid = m.consultant_id ?? m.consultantId
    if (!cid) return
    const owner = consultants.find(c => c.id === cid)?.full_name || m.consultant_name || 'this consultant'
    if (!window.confirm(`Promote ${owner} to lead a sub-project under "${project.name}"? They'll be able to create their own sub-project, add its milestones and assign their own consultants — while staying a consultant on this project.`)) return
    setPromoteBusyId(m.id)
    const res = await promoteMilestoneLead(project.id, m.id, cid)
    setPromoteBusyId(null)
    if (res.success) {
      alert(res.detail || 'Consultant promoted to sub-project lead.')
      api.get(`/projects/${id}/members`).then(r => setMembers(r.data?.members || [])).catch(() => {})
    } else {
      alert(res.error || 'Could not promote this consultant.')
    }
  }

  // Budget Overview card — shown in the Overview tab for Finance, and in the Budget tab
  // for everyone else (moved per Mam so each role reads budget in one consistent place).
  // Actuals/Hybrid projects (Mam #19): the fixed figure is the Total Budget, plus a separate
  // "Actuals" row that starts at $0 and grows as reimbursable expenses are paid.
  const _budgetType = project.budget_type || project.budgetType || 'Fixed'
  const _isActualsProject = ['Actual', 'Hybrid'].includes(_budgetType)
  const _actualsPaid = (milestones || [])
    .filter(m => _budgetType === 'Actual' || (m.payment_mode || m.paymentMode) === 'Actual')
    .filter(m => m.status === 'Paid')
    .reduce((s, m) => s + (m.claimed_amount || m.claimedAmount || 0), 0)

  // A Consultant sees only THEIR OWN milestone(s)' figures, never the whole project's total/
  // approved/remaining budget — mirrors crud.effective_milestone_amount's Fixed vs Actual
  // branching (a Fixed-mode milestone is worth its payment_amount; an Actual-mode one is worth
  // what's been claimed).
  const isConsultantRoleForBudget = user?.role === userRoles.CONSULTANT
  const _myMilestones = (milestones || []).filter(m => (m.consultant_id ?? m.consultantId) === (user?.id ?? user?.user_id))
  const _myEffectiveAmount = (m) => {
    const mode = _budgetType === 'Actual' ? 'Actual' : (_budgetType === 'Hybrid' ? (m.payment_mode || m.paymentMode || 'Fixed') : 'Fixed')
    const claimed = m.claimed_amount ?? m.claimedAmount
    if (mode === 'Actual') return claimed != null ? claimed : (m.payment_amount ?? m.paymentAmount ?? 0)
    return m.payment_amount ?? m.paymentAmount ?? 0
  }
  const _myContractValue = _myMilestones.reduce((s, m) => s + _myEffectiveAmount(m), 0)
  const _myPaid = _myMilestones.filter(m => m.status === 'Paid').reduce((s, m) => s + _myEffectiveAmount(m), 0)
  const _myRemaining = Math.max(0, _myContractValue - _myPaid)

  const budgetOverviewCard = isConsultantRoleForBudget ? (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('my_budget')}</h3>
      <p className="text-xs text-gray-400 mb-3">{t('my_figures_note')}</p>
      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-600">{t('my_contract_value')}</span>
            <span className="text-sm font-medium">${_myContractValue.toLocaleString()}</span>
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-600">{t('paid_to_me')}</span>
            <span className="text-sm font-medium">${_myPaid.toLocaleString()}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full"
              style={{ width: `${_myContractValue ? (_myPaid / _myContractValue) * 100 : 0}%` }}
            ></div>
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">{t('remaining')}</span>
            <span className="text-sm font-medium">${_myRemaining.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('budget_overview')}</h3>
      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-600">Total Budget{_isActualsProject ? ' (fixed)' : ''}</span>
            <span className="text-sm font-medium">${(project.total_budget || project.totalBudget || 0).toLocaleString()}</span>
          </div>
        </div>
        {_isActualsProject && (
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600">{t('actuals_paid')}</span>
              <span className="text-sm font-medium">${_actualsPaid.toLocaleString()}</span>
            </div>
            <p className="text-[11px] text-gray-400">Starts at $0 and grows as reimbursable expenses are approved &amp; paid.</p>
          </div>
        )}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-600">
              {(project.approved_budget || project.approvedBudget) > 0 ? 'Approved' : 'Pending Approval'}
            </span>
            <span className="text-sm font-medium">
              {(project.approved_budget || project.approvedBudget) > 0
                ? `$${(project.approved_budget || project.approvedBudget).toLocaleString()}`
                : <span className="text-amber-600 text-xs">awaiting admin approval</span>}
            </span>
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-600">{t('spent')}</span>
            <span className="text-sm font-medium">${(project.spent_budget || project.spentBudget || 0).toLocaleString()}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full"
              style={{ width: `${(project.approved_budget || project.approvedBudget) ? ((project.spent_budget || project.spentBudget || 0) / (project.approved_budget || project.approvedBudget)) * 100 : 0}%` }}
            ></div>
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">{t('remaining')}</span>
            {/* Compute from approved − spent (the API's remaining_budget isn't always mapped),
                clamped at 0, so this never wrongly shows $0 when budget is left. */}
            <span className="text-sm font-medium">${Math.max(0,
              (project.approved_budget ?? project.approvedBudget ?? 0)
              - (project.spent_budget ?? project.spentBudget ?? 0)
            ).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="p-6 space-y-6">
      {/* Feedback toast */}
      {feedback && (
        <div
          className={`px-4 py-3 rounded-lg text-sm border ${feedback.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}
          onClick={() => setFeedback(null)}
        >
          {feedback.text}
        </div>
      )}

      {/* Lifecycle progress map — shows current stage 1-8 */}
      <ProjectLifecycleMap project={project} refreshKey={lifecycleRefreshKey} />

      {/* header */}
      <div className="card">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 gap-4">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <Link to="/projects" className="text-gray-500 hover:text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </Link>
                <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
              </div>
              {/* Canonical Project ID (ours) + the client's own reference if provided */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {(project.project_number || project.projectNumber) && (
                  <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100">
                    ID: {project.project_number || project.projectNumber}
                  </span>
                )}
                {(project.external_ref || project.externalRef) && (
                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                    Client Ref: {project.external_ref || project.externalRef}
                  </span>
                )}
              </div>
              <p className="text-gray-600 mb-4">{project.description}</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
                {/* Priority is internal management info — hidden from Consultants */}
                {user?.role !== userRoles.CONSULTANT && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(project.priority)}`}>
                    {project.priority || "Medium"} Priority
                  </span>
                )}
                {/* AI health score (on demand) */}
                <AIHealthBadge projectId={project.id} />
                <span className="text-sm text-gray-500">
                  Category: {project.category || "General"}
                </span>
              </div>
            </div>
            {/* ── Header action bar ─────────────────────────────── */}
            <div className="flex items-center gap-2 flex-wrap">

              {/* Export Report — small secondary, always visible */}
              <button
                onClick={exportReport}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>

              {/* Whoever can manage this project. Shown on every status, including Archived. */}
              {(canEditProject || user?.role === userRoles.ADMIN) && (
                <button
                  onClick={exportEverything}
                  disabled={recordsBusy}
                  title="Every record and document for this project, in one file"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  {recordsBusy ? 'Building…' : 'Export all records'}
                </button>
              )}

              {/* ALWAYS REACHABLE, ON EVERY STATUS.
                  The closure screen was linked from further down this page only while a project
                  was In Progress with everything invoiced, or Pending Closure, or Closed. Every
                  other state — Planning, Archived, Cancelled — had no route to it at all, so the
                  only way in was knowing the URL. Not hidden behind the prerequisites either:
                  that screen lists what is still outstanding, which is the answer somebody is
                  looking for when they cannot close a project yet. */}
              {(canEditProject || user?.role === userRoles.ADMIN) && (
                <button
                  onClick={() => navigate(`/projects/${id}/closure`)}
                  title="Closure, archiving and retention for this project"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  Closure
                </button>
              )}

              {/* Said on the page, not swallowed. An export that came back short has to say so
                  here — the only other copy of that fact is inside a zip nobody has opened. */}
              {recordsNote && (
                <span className={`text-xs px-2 py-1 rounded border ${recordsNote.bad
                  ? 'text-amber-800 bg-amber-50 border-amber-200'
                  : 'text-green-700 bg-green-50 border-green-200'}`}>
                  {recordsNote.text}
                </span>
              )}

              {/* Edit Project — small secondary, only for PM/Admin. Hidden on terminal statuses:
                  the backend now refuses edits on Archived/Cancelled projects (this endpoint was
                  previously the one route missing that guard), so offering the button would just
                  produce an error on save. Matches the status filter used on the button below. */}
              {canEditProject && !['Cancelled', 'Archived'].includes(project.status) && (
                <button
                  onClick={openEditModal}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
              )}

              {/* Cancel project (Admin, reversible) — only while active, not on terminal states */}
              {user?.role === userRoles.ADMIN && !['Cancelled', 'Archived', 'Closed', 'Rejected'].includes(project.status) && (
                <button
                  onClick={cancelProject}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50"
                >
                  ⏸️ Cancel project
                </button>
              )}

              {/* ── CONTRACT ACTION — the most important CTA ── */}
              {user?.role === userRoles.ADMIN && ['In Progress', 'Planning'].includes(project.status) && (() => {
                // No contract exists yet
                if (!projectContract) {
                  return (
                    <button
                      onClick={generateContract}
                      disabled={generatingContract}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 shadow-sm"
                    >
                      {generatingContract ? (
                        <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Generating…</>
                      ) : (
                        <>📄 Generate Contract</>
                      )}
                    </button>
                  )
                }

                // Contract exists — show status badge + View link
                const contractColors = {
                  Active: 'bg-green-100 text-green-800 border-green-200',
                  'Pending Signature': 'bg-blue-100 text-blue-800 border-blue-200',
                  'Pending Review': 'bg-amber-100 text-amber-800 border-amber-200',
                  Draft: 'bg-gray-100 text-gray-700 border-gray-200',
                  Expired: 'bg-gray-100 text-gray-500 border-gray-200',
                  Terminated: 'bg-red-100 text-red-700 border-red-200',
                }
                const contractIcons = {
                  Active: '✅', 'Pending Signature': '✍️', 'Pending Review': '👀',
                  Draft: '📝', Expired: '📦', Terminated: '❌',
                }
                return (
                  <div className="flex items-center gap-2">
                    <span className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border ${contractColors[projectContract.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      {contractIcons[projectContract.status] || '📄'} Contract: {projectContract.status}
                    </span>
                    <button
                      onClick={() => navigate(`/contracts/${projectContract.id}`)}
                      className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                    >
                      View Contract →
                    </button>
                    {/* Regenerate only for Draft — rare edge case */}
                    {projectContract.status === 'Draft' && (
                      <button
                        onClick={generateContract}
                        disabled={generatingContract}
                        className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        title={t('t_regenerate')}
                      >
                        {generatingContract ? '…' : '↺ Regenerate'}
                      </button>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>

          {/* Progress Bar. For a consultant, show THEIR OWN progress (share of their milestones
              that are Paid/complete), not the whole-project figure — so it reflects their slice.
              Everyone else sees the project-level progress. */}
          {(() => {
            const myDone = _myMilestones.filter(m => m.status === 'Paid').length
            const myProgress = _myMilestones.length ? Math.round((myDone / _myMilestones.length) * 100) : 0
            const showMine = isConsultantRoleForBudget && _myMilestones.length > 0
            const label = showMine ? 'My Progress' : 'Overall Progress'
            const pct = showMine ? myProgress : (project.progress || 0)
            return (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <span className="text-sm font-bold text-gray-900">{pct}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  ></div>
                </div>
                {showMine && (
                  <p className="text-[11px] text-gray-400 mt-1">{t('based_on_your_milestones')}</p>
                )}
              </div>
            )
          })()}
        </div>
      </div>

      {/* Archived banner + retention card */}
      {project.status === 'Archived' && (
        <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🗄️</span>
            <div className="flex-1">
              <p className="font-semibold text-indigo-900">
                Archived{retention?.project_archived_at ? ` on ${formatDate(retention.project_archived_at)}` : ''} — this project is read-only.
              </p>
              {retention && (
                <p className="text-sm text-indigo-800 mt-1">
                  Deliverables retained until <strong>{retention.deliverables?.next_expiry_date || '—'}</strong>;
                  {' '}documents until <strong>{retention.documents?.next_expiry_date || '—'}</strong>.
                  {' '}Team members can view files; downloading is restricted to the PM/Admin.
                </p>
              )}
              {/* Admin retention/purge controls */}
              {user?.role === userRoles.ADMIN && retention && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[['deliverables', `Deliverables (${retention.deliverable_retention_years ?? 10} yr)`], ['documents', `Documents (${retention.document_retention_years ?? 2} yr)`], ['financial', `Money records (${retention.financial_retention_years ?? 3} yr)`]].map(([key, label]) => {
                    const b = retention[key] || {}
                    return (
                      <div key={key} className="rounded border border-indigo-200 bg-white p-3 text-sm">
                        <p className="font-medium text-gray-800">{label}</p>
                        <p className="text-gray-600 mt-1">
                          {b.archived || 0} archived · {b.purged || 0} purged · <span className={b.expired ? 'text-red-600 font-semibold' : ''}>{b.expired || 0} expired</span>
                        </p>
                        <button
                          onClick={() => purgeArtifacts(key)}
                          disabled={purging || !(b.expired > 0)}
                          className="mt-2 px-3 py-1.5 text-xs font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {purging ? 'Purging…' : `Purge expired ${key === 'financial' ? 'money records' : key}`}
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
              {user?.role === userRoles.ADMIN && retention?.fixity && (
                <div className="mt-3 rounded border border-indigo-200 bg-white p-3 text-sm">
                  <p className="font-medium text-gray-800">Integrity</p>
                  <p className="text-gray-600 mt-1">
                    {retention.fixity.sealed > 0
                      ? <>
                          {retention.fixity.sealed} file{retention.fixity.sealed === 1 ? '' : 's'} fingerprinted
                          {retention.fixity.sealed_at ? ` on ${formatDate(retention.fixity.sealed_at)}` : ''}
                          {retention.fixity.verified_at ? `; last checked ${formatDate(retention.fixity.verified_at)}` : '; not checked yet'}
                          {retention.fixity.problems > 0 && <span className="text-red-600 font-semibold"> · {retention.fixity.problems} not as archived</span>}
                          {retention.fixity.destroyed > 0 && ` · ${retention.fixity.destroyed} destroyed after retention`}
                        </>
                      : retention.fixity.destroyed > 0
                        ? `${retention.fixity.destroyed} file${retention.fixity.destroyed === 1 ? ' was' : 's were'} fingerprinted and ${retention.fixity.destroyed === 1 ? 'has' : 'have'} since been destroyed after retention (see the disposal record). Nothing is left to check.`
                        : 'No files are fingerprinted yet. Archiving does this by itself; use Fingerprint files for a project archived earlier.'}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button onClick={() => runFixity('verify')} disabled={fixityBusy || !(retention.fixity.sealed > 0)}
                      className="px-3 py-1.5 text-xs font-semibold rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40">
                      {fixityBusy ? 'Working…' : 'Check integrity'}
                    </button>
                    <button onClick={() => runFixity('seal')} disabled={fixityBusy}
                      className="px-3 py-1.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                      Fingerprint files
                    </button>
                    <button onClick={downloadDisposal}
                      className="px-3 py-1.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50">
                      Download disposal record
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancelled (on-hold) banner — reversible; Admin can reopen */}
      {project.status === 'Cancelled' && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⏸️</span>
            <div className="flex-1">
              <p className="font-semibold text-amber-900">{t('cancelled_readonly')}</p>
              {project.supplementaryFields?.cancel_reason || project.supplementary_fields?.cancel_reason ? (
                <p className="text-sm text-amber-800 mt-1">Reason: {project.supplementaryFields?.cancel_reason || project.supplementary_fields?.cancel_reason}</p>
              ) : null}
              {user?.role === userRoles.ADMIN && (
                <button
                  onClick={reopenProject}
                  className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700"
                >
                  ▶️ Reopen project
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex space-x-8 px-6 min-w-max">
            {tabs.filter(tab => projectFlow || !['deliverables', 'budget'].includes(tab.id)).map((tab) => {
              // Show dot on Deliverables tab when there's a pending action
              const tabDot = tab.id === 'deliverables' && deliverablesDot && activeTab !== 'deliverables'
                ? deliverablesDot : null
              return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id)
                  setUrlParams({ tab: tab.id, ...(tab.id === 'documents' ? {} : { view: null, dossier: null }) })
                  // Recompute dot when opening deliverables tab
                  if (tab.id === 'deliverables') reloadDeliverablesDot()
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                </svg>
                <span>{tab.name}</span>
                {/* Tab-level dot — guides user to Deliverables when action is needed */}
                {tabDot && (
                  <span className={`w-2 h-2 rounded-full ${tabDot.color} ${tabDot.pulse ? 'animate-pulse' : ''}`}
                    title={tabDot.label} />
                )}
              </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('project_information')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{tc('labels.start_date')}</p>
                      <p className="text-base text-gray-900">
                        {(project.start_date || project.startDate)
                          ? formatDate(project.start_date || project.startDate)
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{tc('labels.end_date')}</p>
                      <p className="text-base text-gray-900">
                        {(project.end_date || project.endDate)
                          ? formatDate(project.end_date || project.endDate)
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{tc('roles.Project Manager')}</p>
                      <p className="text-base text-gray-900">{[
                        teamNames.pm || project.projectManager || 'Not Assigned',
                        ...coManagers.map(m => m.name),
                      ].join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Consultant{(() => {
                        // All consultants who own a milestone on this project (multi-consultant).
                        const names = [...new Set((milestones || [])
                          .map(m => consultants.find(c => c.id === (m.consultant_id ?? m.consultantId))?.full_name)
                          .filter(Boolean))]
                        return names.length > 1 ? 's' : ''
                      })()}</p>
                      <p className="text-base text-gray-900">{(() => {
                        const names = [...new Set((milestones || [])
                          .map(m => consultants.find(c => c.id === (m.consultant_id ?? m.consultantId))?.full_name)
                          .filter(Boolean))]
                        if (names.length) return names.join(', ')
                        return teamNames.consultant || project.consultant || 'Not Assigned'
                      })()}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{t('risk_level')}</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        (project.risk_level || project.riskLevel) === 'High' ? 'bg-red-100 text-red-800' :
                        (project.risk_level || project.riskLevel) === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'}`}>
                        {project.risk_level || project.riskLevel || 'Low'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Milestones list */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{t('milestones')}</h3>
                    {canEditProject && (
                      <button
                        onClick={() => { setMilestoneForm(BLANK_MILESTONE); setAttendeeOpen(false); setAttendeeSearch(''); setShowAddMilestoneModal(true) }}
                        className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100"
                      >
                        + Add Milestone
                      </button>
                    )}
                  </div>
                  {/* Consultant: Accept / Reject each offered (or previously declined) milestone.
                      Declined ones stay here so, after the PM convinces them in the chat, they
                      can still accept. Notes + attachments go in each milestone's chat. */}
                  {user?.role === 'Consultant' && milestones.some(m => ['Offered', 'Declined'].includes(m.scope_status) && m.consultant_id === user?.id) && (
                    <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-amber-900">{t('milestones_offered')}</p>
                      <p className="text-xs text-amber-700 mb-3">Accept the ones you'll deliver, or reject with a reason. Use the chat on each to add notes/attachments or discuss with the manager. A contract is issued for what you accept, once the manager approves.</p>
                      <div className="space-y-3">
                        {milestones.filter(m => ['Offered', 'Declined'].includes(m.scope_status) && m.consultant_id === user?.id).map(m => (
                          <div key={m.id} className="bg-white border border-amber-200 rounded-lg p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{m.title}</p>
                                {(m.payment_amount || m.amount_released_on_completion) > 0 && (
                                  <p className="text-xs text-gray-600">${Number(m.payment_amount || m.amount_released_on_completion).toLocaleString()}{m.due_date ? ` · due ${formatDate(m.due_date)}` : ''}</p>
                                )}
                                {m.scope_status === 'Declined' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">{t('you_declined')}</span>}
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <button
                                  onClick={() => respondMilestone(m.id, 'accept')}
                                  disabled={respondingId === m.id}
                                  className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded hover:bg-green-700 disabled:opacity-50"
                                >✓ Accept</button>
                                {m.scope_status !== 'Declined' && (
                                  <button
                                    onClick={() => respondMilestone(m.id, 'reject')}
                                    disabled={respondingId === m.id}
                                    className="px-3 py-1.5 border border-red-300 text-red-700 text-xs font-semibold rounded hover:bg-red-50 disabled:opacity-50"
                                  >✕ Reject</button>
                                )}
                              </div>
                            </div>
                            <div className="mt-2">
                              <MilestoneChat milestoneId={m.id} projectId={id} milestoneName={m.title} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Consultant: selection awaiting approval */}
                  {user?.role === 'Consultant' && milestones.some(m => m.scope_status === 'Accepted' && m.consultant_id === user?.id) && (
                    <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                      ⏳ Your milestone selection is awaiting manager approval. Your contract will be issued once it's approved.
                    </div>
                  )}

                  {/* PM/Admin: milestones a consultant DECLINED — discuss in the chat to convince them */}
                  {canEditProject && (() => {
                    const declined = milestones.filter(m => m.scope_status === 'Declined' && m.consultant_id)
                    if (!declined.length) return null
                    return (
                      <div className="mb-3 space-y-2">
                        {declined.map(m => (
                          <div key={m.id} className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-sm font-semibold text-red-900">Declined: {m.title}</p>
                            <p className="text-xs text-red-700 mb-2">The consultant declined this milestone. Use the chat to discuss and convince them, or re-offer it above.</p>
                            <MilestoneChat milestoneId={m.id} projectId={id} milestoneName={m.title} />
                          </div>
                        ))}
                      </div>
                    )
                  })()}

                  {/* PM/Admin: approve a consultant's selected milestones → issues their contract */}
                  {canEditProject && (() => {
                    const accepted = milestones.filter(m => m.scope_status === 'Accepted' && m.consultant_id)
                    if (!accepted.length) return null
                    const byC = {}
                    accepted.forEach(m => { (byC[m.consultant_id] = byC[m.consultant_id] || []).push(m) })
                    return (
                      <div className="mb-3 space-y-2">
                        {Object.entries(byC).map(([cid, list]) => {
                          const cName = consultants.find(c => c.id === parseInt(cid))?.full_name
                            || list[0]?.consultant_name || `Consultant #${cid}`
                          return (
                          <div key={cid} className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <p className="text-sm font-semibold text-green-900"><span className="font-bold">{cName}</span> selected {list.length} milestone(s) — approve to issue their contract</p>
                            <ul className="text-xs text-green-800 mt-1 list-disc ml-5">
                              {list.map(m => <li key={m.id}>{m.title}</li>)}
                            </ul>
                            <button
                              onClick={() => approveSelectionFor(parseInt(cid))}
                              disabled={approvingFor === parseInt(cid)}
                              className="mt-2 px-4 py-1.5 bg-green-600 text-white text-xs font-semibold rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {approvingFor === parseInt(cid) ? 'Approving…' : '✓ Approve & Generate Contract'}
                            </button>
                          </div>
                          )
                        })}
                      </div>
                    )
                  })()}

                  {milestones.length === 0 ? (
                    <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4 text-sm text-gray-500 text-center">
                      No milestones yet.{canEditProject && ' Click "+ Add Milestone" to add the first one.'}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {milestones.map((m, i) => (
                        <div key={m.id} className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 ${
                                m.status === 'Paid' ? 'bg-green-500 text-white' :
                                m.consultant_accepted ? 'bg-blue-500 text-white' :
                                'bg-gray-200 text-gray-600'}`}>
                                {i + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800">{m.title}</p>
                                {/* Owner name — so duplicate-titled copies (e.g. a workshop
                                    offered to several consultants) are distinguishable. Shown to
                                    PM/Admin whenever a consultant owns this milestone. */}
                                {canEditProject && (m.consultant_id ?? m.consultantId) && (() => {
                                  const owner = consultants.find(c => c.id === (m.consultant_id ?? m.consultantId))?.full_name
                                    || m.consultant_name
                                  return owner ? (
                                    <span className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-[11px] font-medium text-indigo-700">
                                      👤 {owner}
                                    </span>
                                  ) : null
                                })()}
                                {m.due_date && <p className="text-xs text-gray-500">Due: {formatDate(m.due_date)}</p>}
                                {m.brief_instructions && (
                                  <p className="text-xs text-gray-600 mt-1 italic line-clamp-2">"{m.brief_instructions}"</p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              {(m.payment_amount || m.amount_released_on_completion) > 0 && (
                                <p className="text-sm font-semibold text-gray-800">${Number(m.payment_amount || m.amount_released_on_completion).toLocaleString()}</p>
                              )}
                              {m.scope_status && m.scope_status !== 'Unassigned' && (
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                  m.scope_status === 'Approved' ? 'bg-emerald-100 text-emerald-700' :
                                  m.scope_status === 'Accepted' ? 'bg-blue-100 text-blue-700' :
                                  'bg-amber-100 text-amber-700'}`}>
                                  {m.scope_status === 'Offered' ? 'Offered' : m.scope_status === 'Accepted' ? 'Selected — awaiting approval' : 'In contract'}
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                m.status === 'Paid' ? 'bg-green-100 text-green-700' :
                                m.consultant_accepted ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'}`}>
                                {m.status === 'Paid' ? 'Paid' : m.consultant_accepted ? 'Approved' : m.status || 'Pending'}
                              </span>
                            </div>
                          </div>

                          {/* PM/Admin actions on each milestone */}
                          {canEditProject && (
                            <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-blue-100">
                              {/* Once the consultant has accepted the scope (or it's approved into a
                                  contract) the milestone is fixed for everyone, Admin included — the
                                  backend refuses both edit and delete. Show the reason instead of a
                                  button that fails on click. Mirrors ProjectModal's locked state. */}
                              {['Accepted', 'Approved'].includes(m.scope_status || '') ? (
                                <span className="text-[11px] px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700"
                                  title="Agreed with the consultant — raise a contract amendment to change the scope.">
                                  🔒 agreed with consultant — locked
                                </span>
                              ) : (
                                <>
                                  <button
                                    onClick={() => openEditMilestone(m)}
                                    className="text-xs px-2.5 py-1 bg-white text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    onClick={() => deleteMilestone(m)}
                                    className="text-xs px-2.5 py-1 bg-white text-red-600 border border-red-200 rounded hover:bg-red-50"
                                  >
                                    🗑️ Delete
                                  </button>
                                </>
                              )}
                              {m.has_brief ? (
                                <button
                                  onClick={() => downloadBrief(m.id)}
                                  className="text-xs px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100"
                                >
                                  📎 View Brief
                                </button>
                              ) : null}
                              <label className="cursor-pointer">
                                <span className={`text-xs px-2.5 py-1 bg-white text-blue-700 border border-blue-200 rounded hover:bg-blue-50 ${briefUploading === m.id ? 'opacity-50' : ''}`}>
                                  {briefUploading === m.id ? '⏳ Uploading…' : m.has_brief ? '🔄 Replace Brief' : '📤 Upload Brief'}
                                </span>
                                <input
                                  type="file"
                                  accept=".pdf,.docx,.doc,.xlsx,.pptx,.txt"
                                  className="hidden"
                                  disabled={briefUploading === m.id}
                                  onChange={(e) => e.target.files?.[0] && uploadBrief(m.id, e.target.files[0])}
                                />
                              </label>
                              {/* Offer this SAME milestone to more consultants — each gets a fresh
                                  copy (same title/amount/deadline), like the workshop flow. */}
                              <button
                                onClick={() => { setOfferMoreFor(m); setOfferMoreIds([]); setOfferMoreSearch(''); setOfferMoreOpen(false) }}
                                className="text-xs px-2.5 py-1 bg-white text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-50"
                                title="Give this same milestone to other consultants — each gets their own copy"
                              >
                                👥 Offer to more consultants
                              </button>
                              {/* v3.2: promote this milestone's consultant to lead their own sub-project
                                  under this program. Only on a top-level project, once a consultant owns it. */}
                              {!isSubProject && (m.consultant_id ?? m.consultantId) && (
                                <button
                                  onClick={() => handlePromoteLead(m)}
                                  disabled={promoteBusyId === m.id}
                                  className="text-xs px-2.5 py-1 bg-white text-purple-700 border border-purple-200 rounded hover:bg-purple-50 disabled:opacity-50"
                                  title="Promote this consultant to lead their own sub-project under this program"
                                >
                                  {promoteBusyId === m.id ? '⏳ Promoting…' : '⭐ Make sub-project lead'}
                                </button>
                              )}
                            </div>
                          )}
                          {/* Consultant: show download brief button if PM uploaded one */}
                          {!canEditProject && m.has_brief && (
                            <div className="mt-2 pt-2 border-t border-blue-100">
                              <button
                                onClick={() => downloadBrief(m.id)}
                                className="text-xs px-2.5 py-1 bg-white text-blue-700 border border-blue-200 rounded hover:bg-blue-50"
                              >
                                📎 Download Brief / Instructions
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                {/* Budget lives in the Budget tab for everyone except Finance, who read it here. */}
                {isFinance && budgetOverviewCard}
              </div>

              {/* ── Closure Action Panel ───────────────────────────────────────
                  Auto-appears for PM once all paid milestones have invoices.
                  Auto-appears for Admin when project is Pending Closure. */}
              {(() => {
                // A milestone that carries money: a Fixed figure (payment_amount) OR an
                // Actual/reimbursable claim (claimed_amount) — the latter is 0 on payment_amount
                // for Actual projects, which previously hid the closure button entirely.
                const paidMs = milestones.filter(m =>
                  (m.payment_amount || m.amount_released_on_completion || m.claimed_amount || 0) > 0
                )
                // "Ready to close" = every money milestone has been invoiced (invoice_id set,
                // which happens on invoice trigger) — or is already Paid.
                const allInvoiced = paidMs.length > 0 && paidMs.every(m => m.invoice_id || m.status === 'Paid')
                const isPMRole = user?.role === 'Project Manager'
                const isAdminRole = user?.role === 'Admin'
                const projectStatus = project.status

                // PM (or Admin, who overrides everything): show when In Progress + all
                // money milestones invoiced/paid. Admin can initiate if the PM is unavailable.
                if ((isPMRole && canEditProject || isAdminRole) && projectStatus === 'In Progress' && allInvoiced) {
                  return (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                          <span className="text-2xl">🎉</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-green-900">{t('all_complete')}</h3>
                          <p className="text-xs text-green-700 mt-1">All deliverables approved and invoices raised. You can now initiate the project closure.</p>
                          <button
                            onClick={() => window.location.href = `/projects/${id}/closure`}
                            className="mt-3 px-5 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700"
                          >
                            🔒 Initiate Project Closure →
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                }

                // Admin: show when Pending Closure
                if (isAdminRole && projectStatus === 'Pending Closure') {
                  return (
                    <div className="mt-4 bg-orange-50 border border-orange-200 rounded-xl p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                          <span className="text-2xl">⏳</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-orange-900">{t('closure_requested')}</h3>
                          <p className="text-xs text-orange-700 mt-1">{t('confirm_closure')}</p>
                          <button
                            onClick={() => window.location.href = `/projects/${id}/closure`}
                            className="mt-3 px-5 py-2 bg-orange-600 text-white text-sm font-semibold rounded-lg hover:bg-orange-700"
                          >
                            ✅ Confirm Project Closure →
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                }

                // Admin: show Archive button when Closed
                if (isAdminRole && projectStatus === 'Closed') {
                  return (
                    <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                          <span className="text-2xl">📦</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{t('closed_archive_ready')}</h3>
                          <p className="text-xs text-gray-600 mt-1">Archiving makes the project read-only for long-term compliance retention.</p>
                          <button
                            onClick={() => window.location.href = `/projects/${id}/closure`}
                            className="mt-3 px-5 py-2 bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800"
                          >
                            📦 Archive Project →
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                }

                return null
              })()}

              {/* v3.2 nested projects — sub-projects under this program (top-level only) */}
              {!isSubProject && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 mt-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{t('sub_projects')}</h3>
                      <p className="text-xs text-gray-500">A large milestone can be run as its own sub-project by a promoted lead. Program totals roll up here.</p>
                    </div>
                    {canCreateSubproject && (
                      <button onClick={goCreateSubproject} className="text-sm px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
                        + Create sub-project
                      </button>
                    )}
                  </div>

                  {rollup && rollup.children_count > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('program_budget')}</p>
                        <p className="text-sm font-semibold text-gray-800">${Number(rollup.program.total_budget || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('program_spent')}</p>
                        <p className="text-sm font-semibold text-gray-800">${Number(rollup.program.spent_budget || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('program_remaining')}</p>
                        <p className="text-sm font-semibold text-gray-800">${Number(rollup.program.remaining_budget || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('program_progress')}</p>
                        <p className="text-sm font-semibold text-gray-800">{rollup.program.progress || 0}%</p>
                      </div>
                    </div>
                  )}

                  {subprojects.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No sub-projects yet.{canCreateSubproject ? ' Promote a milestone’s consultant to “sub-project lead”, then create one.' : ''}</p>
                  ) : (
                    /* Program tree: this project is the root, its sub-projects hang off it as
                       branches. Nesting is capped at one level backend-side, so the tree is
                       always exactly two deep — a gutter + ├─/└─ connectors read clearer here
                       than a generic recursive tree component. */
                    <div>
                      {/* root node = this project */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-purple-500 text-sm">📂</span>
                        <span className="text-sm font-semibold text-gray-900 truncate">{project.name}</span>
                        {(project.project_number || project.projectNumber) && (
                          <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 shrink-0">
                            {project.project_number || project.projectNumber}
                          </span>
                        )}
                        <span className="text-[11px] text-gray-400 shrink-0">
                          · {subprojects.length} sub-project{subprojects.length > 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* branches */}
                      <div className="pl-2">
                        {subprojects.map((sp, idx) => {
                          const isLast = idx === subprojects.length - 1
                          return (
                            <div key={sp.id} className="flex items-stretch">
                              {/* connector gutter */}
                              <div className="relative w-6 shrink-0">
                                <span className={`absolute left-2 top-0 w-px bg-purple-200 ${isLast ? 'h-5' : 'h-full'}`} />
                                <span className="absolute left-2 top-5 h-px w-3 bg-purple-200" />
                              </div>
                              <button
                                onClick={() => navigate(`/projects/${sp.id}`)}
                                className="flex-1 min-w-0 text-left flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-4 py-3 my-1 hover:border-purple-300 hover:bg-purple-50/40 transition"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate flex items-center gap-2">
                                    <span className="truncate">{sp.name}</span>
                                    {sp.project_number && (
                                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 shrink-0">
                                        {sp.project_number}
                                      </span>
                                    )}
                                    {sp.status && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 shrink-0">
                                        {sp.status}
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {sp.project_manager_name ? `Lead: ${sp.project_manager_name}` : 'Sub-project'}
                                  </p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-sm font-semibold text-gray-800">${Number(sp.total_budget || 0).toLocaleString()}</p>
                                  <p className="text-xs text-gray-500">{sp.progress || 0}% done</p>
                                </div>
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{t('project_tasks')}</h3>
                {canEditProject && (
                  <button onClick={openAddTaskModal} className="btn-primary">{t('add_task')}</button>
                )}
              </div>
              <div className="space-y-3">
                {projectTasks.map((task) => (
                  <div key={task.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1 w-full">
                        <h4 className="font-medium text-gray-900">{task.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${task.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            task.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                            {task.status}
                          </span>
                          {task.priority && (
                            <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                          )}
                          {task.due_date && (
                            <span className="text-xs text-gray-500">Due: {formatDate(task.due_date)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {projectTasks.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>{t('no_tasks')}</p>
                    {canEditProject && (
                      <button onClick={openAddTaskModal} className="btn-primary mt-2">{t('create_first_task')}</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'deliverables' && (
            <DeliverablesTab project={project} openMilestoneChatId={deepLinkMilestoneId} highlightId={highlightId} autoConfirmMilestoneId={autoConfirmMilestoneId} isArchived={project.status === 'Archived'} onArtifactChange={reloadRetention} onProjectDataChange={handleProjectDataChange} isCoManager={isCoManager} isApprover={isApproverHere} />
          )}

          {activeTab === 'documents' && (
            <DocumentsTab project={project} highlightId={highlightId} dossierId={dossierParam} isArchived={project.status === 'Archived'} onArtifactChange={reloadRetention} />
          )}

          {activeTab === 'budget' && (
            <div className="max-w-md">{budgetOverviewCard}</div>
          )}

          {activeTab === 'team' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">{t('project_team')}</h3>

              {/* Co-managers / delegated access (#2) — owner PM or Admin can grant a team
                  member write (management) access, or set them back to view-only. */}
              {canEditProject && members.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-800 mb-1">{t('team_access_note')}</h4>
                  <p className="text-xs text-gray-500 mb-1">
                    Everyone here can view the project. Grant <strong>manage</strong> access to let a member
                    run it like a manager — edit milestones, review deliverables and give the <em>PM</em> invoice
                    approval. An <strong>approver</strong> instead <em>checks</em> the work: they counter-sign
                    contracts and give the <em>{tc('roles.Admin')}</em> invoice approval for this project and its sub-projects.
                  </p>
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3">
                    🔒 <strong>{t('mutually_exclusive')}</strong> Nobody may approve work
                    they manage — so whichever one a person holds, the other button is disabled until you revoke it.
                    Only an Admin can make someone an approver.
                  </p>
                  <div className="space-y-2">
                    {members.map(m => (
                      <div key={m.user_id} className="flex items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                          <p className="text-[11px] text-gray-400">{m.role}{m.can_manage ? ' · can manage' : ' · view only'}{m.can_approve ? ' · approver' : ''}</p>
                        </div>
                        {/* Mutually exclusive on this project: whoever manages it can never approve
                            it. Rather than let the two be set together (which silently voided the
                            approver rights), the opposing button is disabled with the reason shown. */}
                        <div className="flex items-center gap-2 shrink-0">
                          {user?.role === 'Admin' && (
                            <button
                              onClick={() => toggleApprover(m.user_id, !m.can_approve)}
                              disabled={!m.can_approve && m.can_manage}
                              title={!m.can_approve && m.can_manage
                                ? `${m.name} manages this project, so they can't also approve it. Revoke manage first.`
                                : 'Approver: may counter-sign contracts and give final invoice approval for this project and its sub-projects (never a project they manage)'}
                              className={`text-xs font-semibold rounded-lg px-3 py-1.5 ${
                                !m.can_approve && m.can_manage
                                  ? 'text-gray-400 bg-gray-50 border border-gray-200 cursor-not-allowed'
                                  : m.can_approve
                                    ? 'text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100'
                                    : 'text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}
                            >
                              {m.can_approve ? 'Revoke approver' : 'Make approver'}
                            </button>
                          )}
                          <button
                            onClick={() => toggleDelegate(m.user_id, !m.can_manage)}
                            disabled={!m.can_manage && m.can_approve}
                            title={!m.can_manage && m.can_approve
                              ? `${m.name} is the approver on this project, so they can't also manage it. Revoke approver first.`
                              : 'Manage: run this project like its manager — edit milestones, review deliverables, give the PM invoice approval'}
                            className={`text-xs font-semibold rounded-lg px-3 py-1.5 ${
                              !m.can_manage && m.can_approve
                                ? 'text-gray-400 bg-gray-50 border border-gray-200 cursor-not-allowed'
                                : m.can_manage
                                  ? 'text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100'
                                  : 'text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100'}`}
                          >
                            {m.can_manage ? 'Revoke manage' : 'Grant manage'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Consultant Assignment — searchable dropdown */}
              {canEditProject && (() => {
                // PM/Finance already on the team are hidden from their Add dropdowns (adding
                // them twice is a duplicate). "Already on the team" includes THREE sources:
                //   1. the owning PM (project.project_manager_id) — stored on the project, NOT
                //      in the members list;
                //   2. the directly-assigned consultant (project.consultant_id);
                //   3. everyone in the members list and every consultant in team.team.
                // Consultants are NOT hidden from the Offer dropdown (see below) — re-offering
                // new/open milestones to the same consultant is a normal flow.
                const ownerPmId = project.project_manager_id ?? project.projectManagerId
                const ownerConsultantId = project.consultant_id ?? project.consultantId
                const currentConsultantIds = new Set([
                  ...(team?.team || []).map(m => m.consultant_id),
                  ...(members || []).filter(m => m.role === 'Consultant').map(m => m.user_id),
                  ownerConsultantId,
                ].filter(Boolean))
                const currentMemberIds = new Set([
                  ...(members || []).map(m => m.user_id),
                  ownerPmId,
                ].filter(Boolean))
                // Consultants already on the team STAY in the Offer dropdown, labelled —
                // offering them a newly-added/open milestone is a legitimate, common flow
                // (Bharat's 4th-milestone case: hiding them here made re-offering impossible).
                // The backend never re-offers milestones already taken, so this is safe.
                // PM/Finance exclusions stay: adding those twice IS a duplicate.
                const isOnTeam = (id) => currentConsultantIds.has(id)
                // Filter consultants by name / specialization / country / experience so the PM
                // can find the right person fast (same fields as the project team picker).
                const consultantMeta = (c) => [c.specialization, c.country || c.nationality,
                  (c.years_of_experience != null && c.years_of_experience !== '') ? `${c.years_of_experience} years` : ''].filter(Boolean)
                const cf = consultantFilter.trim().toLowerCase()
                const availableConsultants = consultants.filter(c => {
                  if (!cf) return true
                  return [c.full_name, c.consultant_code, c.email, ...consultantMeta(c)].filter(Boolean).join(' ').toLowerCase().includes(cf)
                })
                const availablePmUsers = pmUsers.filter(u => !currentMemberIds.has(u.id))
                const availableFinanceUsers = financeUsers.filter(u => !currentMemberIds.has(u.id))
                // How many milestones are still open to offer. If none, offering a new
                // consultant will 400 ("no available milestones") — so warn + disable up front.
                const openMilestones = (milestones || []).filter(
                  m => (m.scope_status || 'Unassigned') === 'Unassigned'
                )
                const openMilestoneCount = openMilestones.length
                const toggleOfferMs = (mid) => setOfferMilestoneIds(prev =>
                  prev.includes(mid) ? prev.filter(x => x !== mid) : [...prev, mid]
                )
                return (
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-800 mb-1">{t('offer_milestones')}</h4>
                  <p className="text-xs text-gray-500 mb-3">
                    On a multi-consultant project, give <strong>different milestones to each consultant</strong> — tick exactly which ones this consultant should get. Leave all unticked to offer every open milestone. They confirm what they'll take, then you approve to issue their contract. Milestones already taken by someone are never offered again.
                  </p>
                  <form onSubmit={handleAssignConsultant} className="space-y-3">
                    {/* Consultant dropdown — all Active consultants; team members are labelled */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{tc('roles.Consultant')}</label>
                      <input
                        type="text"
                        value={consultantFilter}
                        onChange={(e) => setConsultantFilter(e.target.value)}
                        placeholder={t('ph_filter_people')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white mb-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-[11px] text-gray-400 mb-1">Tip: type a specialization, country, or years of experience to narrow the list.</p>
                      <select
                        value={newConsultantId}
                        onChange={(e) => setNewConsultantId(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— select a consultant —</option>
                        {availableConsultants.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.full_name}{c.consultant_code ? ` · ${c.consultant_code}` : ''}{consultantMeta(c).length ? ` · ${consultantMeta(c).join(' · ')}` : ''}{isOnTeam(c.id) ? ' · already on this project' : ''}
                          </option>
                        ))}
                        {availableConsultants.length === 0 && <option value="" disabled>No consultants match “{consultantFilter}”</option>}
                      </select>
                    </div>

                    {/* Per-milestone picker — tick which open milestones go to THIS consultant */}
                    {openMilestoneCount > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-medium text-gray-600">
                            Which milestones for this consultant?
                          </label>
                          <span className="text-[11px] text-gray-400">
                            {offerMilestoneIds.length ? `${offerMilestoneIds.length} selected` : 'none ticked → offer all open'}
                          </span>
                        </div>
                        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-52 overflow-y-auto">
                          {openMilestones.map(m => (
                            <label key={m.id} className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                              <input
                                type="checkbox"
                                checked={offerMilestoneIds.includes(m.id)}
                                onChange={() => toggleOfferMs(m.id)}
                                className="mt-0.5"
                              />
                              <span className="flex-1 min-w-0">
                                <span className="font-medium text-gray-800">{m.title}</span>
                                {(m.payment_amount ?? m.paymentAmount) > 0 && (
                                  <span className="text-gray-400"> · ${Number(m.payment_amount ?? m.paymentAmount).toLocaleString()}</span>
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {openMilestoneCount === 0 && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        No open milestones left to offer — every milestone on this project is already offered to or taken by a consultant. Add a new milestone first, or the existing consultant must decline one to free it up.
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={assignLoading || !newConsultantId || openMilestoneCount === 0}
                      className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {assignLoading ? 'Offering…' : (offerMilestoneIds.length ? `✓ Offer ${offerMilestoneIds.length} milestone(s)` : '✓ Offer all open milestones')}
                    </button>
                  </form>

                  {/* Assign a Project Manager (co-manager) or Finance (view) to this project's team */}
                  <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('add_pm')}</label>
                      <div className="flex gap-2">
                        <select id="pm-assign-select" defaultValue="" className="flex-1 border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white">
                          <option value="">— select a PM —</option>
                          {availablePmUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                        </select>
                        <button
                          type="button"
                          disabled={assignRoleBusy}
                          onClick={() => { const el = document.getElementById('pm-assign-select'); const nm = el.options[el.selectedIndex]?.text; assignRoleMember(el.value, true, nm, 'a co-manager (PM)'); el.value = '' }}
                          className="px-3 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >Add</button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('add_finance')}</label>
                      <div className="flex gap-2">
                        <select id="fin-assign-select" defaultValue="" className="flex-1 border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white">
                          <option value="">— select Finance —</option>
                          {availableFinanceUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                        </select>
                        <button
                          type="button"
                          disabled={assignRoleBusy}
                          onClick={() => { const el = document.getElementById('fin-assign-select'); const nm = el.options[el.selectedIndex]?.text; assignRoleMember(el.value, false, nm, 'Finance (view access)'); el.value = '' }}
                          className="px-3 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >Add</button>
                      </div>
                    </div>
                  </div>

                  {/* Immediate, in-place confirmation of who's been added (PMs/Finance) so the
                      result shows right here where you clicked — not only in the block above. */}
                  {(() => {
                    const added = (members || []).filter(m => m.role === 'Finance' || (m.role === 'Project Manager' && m.user_id !== (project.project_manager_id ?? project.projectManagerId)))
                    if (added.length === 0) return null
                    return (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('added_to_project')}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {added.map(m => (
                            <span key={m.user_id} className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-1">
                              ✓ {m.name} <span className="text-green-500">· {m.role}{m.can_manage ? ' (co-manager)' : ' (view)'}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )})()}

              {/* Who's working on this project — derived from milestone ownership */}
              {team && team.team && team.team.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-800 mb-3">{t('consultants_on_project')}</h4>
                  <div className="space-y-3">
                    {team.team.map(member => (
                      <div key={member.consultant_id} className="flex items-center gap-3 border border-gray-100 rounded-lg p-3">
                        <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold shrink-0">
                          {(member.name || 'C').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{member.name}</p>
                          {member.consultant_code && (
                            <p className="text-[11px] font-mono text-gray-400 truncate">{member.consultant_code}</p>
                          )}
                          {member.email && <p className="text-xs text-gray-500 truncate">{member.email}</p>}
                        </div>
                        <div className="flex flex-wrap gap-1.5 justify-end">
                          {member.approved > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{member.approved} in contract</span>}
                          {member.accepted > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{member.accepted} selected</span>}
                          {member.offered > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{member.offered} offered</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {team.unassigned_count > 0 && (
                    <p className="text-xs text-gray-500 mt-3">{team.unassigned_count} milestone(s) still open — offer them to a consultant above.</p>
                  )}
                </div>
              )}

              {/* Team cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { role: 'Project Manager', name: teamNames.pm || project.projectManager || 'Not Assigned', avatar: 'PM', color: 'bg-blue-500' },
                  ...coManagers.map(m => ({ role: 'Project Manager (co-manager)', name: m.name, avatar: 'PM', color: 'bg-blue-500' })),
                  { role: 'Consultant', name: teamNames.consultant || project.consultant || 'Not Assigned', avatar: 'CO', color: 'bg-purple-500' }
                ].map((member, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-md transition-shadow">
                    <div className={`h-16 w-16 rounded-full ${member.color} flex items-center justify-center mx-auto mb-3 text-white font-bold text-lg`}>
                      {member.avatar}
                    </div>
                    <h4 className={`font-semibold ${member.name === 'Not Assigned' ? 'text-gray-400 italic' : 'text-gray-900'}`}>{member.name}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{member.role}</p>
                    {member.name !== 'Not Assigned' && (
                      <button
                        onClick={() => contactMember(member)}
                        className="mt-3 px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                      >
                        ✉️ Contact
                      </button>
                    )}
                    {member.name === 'Not Assigned' && member.role === 'Consultant' && canEditProject && (
                      <p className="text-xs text-amber-600 mt-2">↑ Assign a consultant above</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{t('activity_log')}</h3>
                <span className="text-xs text-gray-500 italic">{t('audit_trail')}</span>
              </div>

              {historyLoading ? (
                <div className="flex justify-center p-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
                  No activity recorded yet.
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  <div className="space-y-8">
                    {history.map((item, index) => (
                      <div key={item.id} className="relative pl-10">
                        <div className={`absolute left-0 top-1.5 w-8 h-8 rounded-full border-4 border-white flex items-center justify-center ${item.type === 'success' ? 'bg-green-500' :
                          item.type === 'warning' ? 'bg-amber-500' :
                            item.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
                          }`}>
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        </div>

                        <div className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-1">
                            <h4 className="font-bold text-gray-900">{item.title}</h4>
                            <span className="text-xs font-medium text-gray-400">
                              {formatDateTime(item.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Edit Project Modal ─────────────────────────────────────────── */}
      {showEditModal && editForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <form onSubmit={saveEdit} className="p-6 space-y-4">
              <h2 className="text-xl font-bold text-gray-900">{t('edit_project')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('project_name_req')}</label>
                  <input type="text" required value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.description')}</label>
                  <textarea rows={3} value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.status')}</label>
                  <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {['Planning', 'Pending Approval', 'In Progress', 'On Hold', 'Completed', 'Rejected'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.priority')}</label>
                  <select value={editForm.priority} onChange={e => setEditForm({ ...editForm, priority: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {['Low', 'Medium', 'High'].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.category')}</label>
                  <input type="text" value={editForm.category} onChange={e => setEditForm({ ...editForm, category: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('risk_level')}</label>
                  <select value={editForm.risk_level} onChange={e => setEditForm({ ...editForm, risk_level: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {['Low', 'Medium', 'High'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.start_date')}</label>
                  <input type="date" value={editForm.start_date} onChange={e => setEditForm({ ...editForm, start_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.end_date')}</label>
                  <input
                    type="date"
                    value={editForm.end_date}
                    max={(parentProject?.end_date || parentProject?.endDate) || undefined}
                    onChange={e => setEditForm({ ...editForm, end_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  {(parentProject?.end_date || parentProject?.endDate) && (
                    <p className="text-[11px] text-gray-400 mt-1">
                      Can't be after the parent program ({parentProject.name}) ends on {parentProject.end_date || parentProject.endDate}. Finishing earlier is fine.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('total_budget_dollar')}</label>
                  <input type="number" min="0" step="0.01" value={editForm.total_budget} onChange={e => setEditForm({ ...editForm, total_budget: parseFloat(e.target.value) || 0 })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-4 border-t">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">{tc('actions.cancel')}</button>
                <button type="submit" disabled={editSaving} className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Milestone Modal ───────────────────────────────────────── */}
      {editingMilestone && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <form onSubmit={saveMilestoneEdit} className="p-6 space-y-4">
              <h2 className="text-xl font-bold text-gray-900">{t('edit_milestone')}</h2>
              {(() => {
                const _bt = project.budget_type || project.budgetType || 'Fixed'
                const effType = _bt === 'Actual' ? 'Actual' : (_bt === 'Hybrid' ? msEditForm.paymentMode : 'Fixed')
                const isActual = effType === 'Actual'
                return (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('title_req')}</label>
                      <input type="text" required value={msEditForm.title} onChange={e => setMsEditForm({ ...msEditForm, title: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    {/* Type — mirrors the create wizard. Editable only on a Hybrid project;
                        a Fixed project's milestones are always Fixed, an Actual project's always Actuals. */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('type')}</label>
                      {_bt === 'Hybrid' ? (
                        <select value={msEditForm.paymentMode} onChange={e => setMsEditForm({ ...msEditForm, paymentMode: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                          <option value="Fixed">{t('fixed_lump_sum')}</option>
                          <option value="Actual">{t('actuals_reimbursable')}</option>
                        </select>
                      ) : (
                        <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600">
                          {isActual ? 'Actuals — reimbursable (set by the project budget type)' : 'Fixed — agreed lump sum (set by the project budget type)'}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{isActual ? 'Estimated Amount ($)' : 'Payment Amount ($)'}</label>
                        <input type="number" min="0" step="0.01" value={msEditForm.amount} onChange={e => setMsEditForm({ ...msEditForm, amount: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder={isActual ? 'estimate (actual comes from bills)' : ''} />
                        {isActual && <p className="text-[10px] text-gray-400 mt-1">Actuals are reimbursable — the real amount comes from the bills the consultant attaches.</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.due_date')}</label>
                        <input type="date" value={msEditForm.due_date} onChange={e => setMsEditForm({ ...msEditForm, due_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                    </div>
                    {isActual && (
                      <div className="p-2.5 rounded-lg border border-amber-200 bg-amber-50/60">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-amber-800">{t('system_tentative_budget')} <span className="font-normal text-gray-400">· PM/Admin only</span></label>
                          <span className="text-xs font-bold text-amber-600">${Number(msEditForm.tentativeBudget || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <input type="range" min="0" max="50000" step="500" value={Number(msEditForm.tentativeBudget || 0)}
                          onChange={e => setMsEditForm({ ...msEditForm, tentativeBudget: e.target.value })} className="w-full mt-1.5 accent-amber-500" />
                        <div className="flex items-center gap-1.5 mt-1.5">
                          {[15000, 25000, 50000].map(v => (
                            <button type="button" key={v} onClick={() => setMsEditForm({ ...msEditForm, tentativeBudget: v })}
                              className={`px-2 py-0.5 text-[11px] rounded border ${Number(msEditForm.tentativeBudget) === v ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-300 text-gray-600 hover:bg-white'}`}>${v.toLocaleString()}</button>
                          ))}
                          <input type="number" min="0" step="0.01" value={msEditForm.tentativeBudget ?? ''}
                            onChange={e => setMsEditForm({ ...msEditForm, tentativeBudget: e.target.value })} placeholder={t('ph_custom')}
                            className="flex-1 px-2 py-1 rounded border border-gray-300 text-xs" />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">Consultant bills for this milestone can't exceed this. They never see it.</p>
                      </div>
                    )}
                  </>
                )
              })()}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('written_instructions')}</label>
                <textarea rows={3} value={msEditForm.brief_instructions} onChange={e => setMsEditForm({ ...msEditForm, brief_instructions: e.target.value })} placeholder="e.g. Please submit a 5-page analysis with methodology, findings, and recommendations in PDF format." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <p className="text-[10px] text-gray-400 mt-1">This text appears on the milestone card for the consultant. You can also upload a PDF brief below.</p>
              </div>
              <div className="flex gap-3 justify-end pt-2 border-t">
                <button type="button" onClick={() => setEditingMilestone(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">{tc('actions.cancel')}</button>
                <button type="submit" disabled={msEditSaving} className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                  {msEditSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add Milestone Modal ────────────────────────────────────────── */}
      {showAddMilestoneModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <form onSubmit={submitMilestone} className="p-6 space-y-4">
              <h2 className="text-xl font-bold text-gray-900">{t('add_milestone')}</h2>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('title_req')}</label>
                <input type="text" required value={milestoneForm.title} onChange={e => setMilestoneForm({ ...milestoneForm, title: e.target.value })} placeholder={t('ph_milestone_example')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              {/* Type — mirrors the create-project wizard. Selectable only on a Hybrid project;
                  a Fixed project's milestones are always Fixed, an Actual project's always Actuals.
                  When the effective mode is Actual, no fixed amount is entered (claimed with bills). */}
              {(() => {
                const effMode = _budgetType === 'Actual'
                  ? 'Actual'
                  : (_budgetType === 'Hybrid' ? milestoneForm.paymentMode : 'Fixed')
                return (
                  <>
                    {_budgetType === 'Hybrid' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('type')}</label>
                        <select
                          value={milestoneForm.paymentMode}
                          onChange={e => setMilestoneForm({ ...milestoneForm, paymentMode: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                        >
                          <option value="Fixed">{t('fixed_enter_amount')}</option>
                          <option value="Actual">{t('actuals_claimed')}</option>
                        </select>
                      </div>
                    )}

                    {/* On a Fixed or Actual project the type is not a choice, so there is no
                        dropdown -- which read as "the field is missing". Say what it is and why
                        it cannot be changed here, rather than showing nothing at all. */}
                    {_budgetType !== 'Hybrid' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('type')}</label>
                        <input
                          type="text"
                          disabled
                          readOnly
                          value={effMode === 'Actual' ? t('actuals_claimed') : t('fixed_enter_amount')}
                          className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600"
                        />
                        <p className="text-[11px] text-gray-400 mt-1">
                          Set by the project's budget type ({_budgetType}). Change it on the project to change it here.
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('payment_amount_dollar')}</label>
                        {effMode === 'Actual' ? (
                          <input type="text" value="Actuals · claimed with bills" disabled readOnly className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-amber-700 font-medium" />
                        ) : (
                          <input type="number" min="0" step="0.01" value={milestoneForm.amount} onChange={e => setMilestoneForm({ ...milestoneForm, amount: e.target.value })} placeholder={t('ph_amount_example')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.due_date')}</label>
                        <input type="date" value={milestoneForm.due_date} onChange={e => setMilestoneForm({ ...milestoneForm, due_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                    </div>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      {effMode === 'Actual'
                        ? '💡 Actuals milestone — the consultant claims the real amount with bills at invoice time; no fixed amount is set here.'
                        : '💡 Payment amount is what the consultant gets paid when this milestone\'s deliverables are all approved.'}
                    </p>

                    {/* WHO IS GOING TO DO IT. Deciding the work and deciding who does it is one
                        thought; it used to be two screens — add the milestone here, then find it
                        in the list and offer it. Optional, because a milestone can legitimately
                        be written before anybody is chosen for it. */}
                    {!milestoneForm.isWorkshop && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Consultant <span className="font-normal text-gray-400">(optional — offers it to them straight away)</span>
                        </label>
                        <select
                          value={milestoneForm.consultantId}
                          onChange={e => setMilestoneForm({ ...milestoneForm, consultantId: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                        >
                          <option value="">Leave unassigned for now</option>
                          {consultants.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.full_name}{c.consultant_code ? ` · ${c.consultant_code}` : ''}
                            </option>
                          ))}
                        </select>
                        {milestoneForm.consultantId && (
                          <p className="text-[11px] text-emerald-700 mt-1">
                            They are told as soon as you add it, and can accept or decline.
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )
              })()}

              {/* Workshop / meeting toggle — SAME milestone for several consultants */}
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={milestoneForm.isWorkshop}
                    onChange={e => { if (!e.target.checked) { setAttendeeOpen(false); setAttendeeSearch('') } ; setMilestoneForm({ ...milestoneForm, isWorkshop: e.target.checked, attendeeIds: e.target.checked ? milestoneForm.attendeeIds : [] }) }}
                    className="mt-0.5"
                  />
                  <span className="text-xs text-gray-700">
                    <span className="font-semibold text-indigo-800">{t('workshop_meeting')}</span> — this is the <strong>same</strong> milestone for several consultants. Each attendee gets their own copy (same title, amount &amp; deadline) and submits their own deliverables &amp; bills.
                  </span>
                </label>

                {milestoneForm.isWorkshop && (() => {
                  const q = attendeeSearch.trim().toLowerCase()
                  const filtered = consultants.filter(c =>
                    !q ||
                    (c.full_name || '').toLowerCase().includes(q) ||
                    (c.consultant_code || '').toLowerCase().includes(q)
                  )
                  const selectedNames = consultants
                    .filter(c => milestoneForm.attendeeIds.includes(c.id))
                    .map(c => c.full_name)
                  const toggle = (cid, checked) => setMilestoneForm(f => ({
                    ...f,
                    attendeeIds: checked ? f.attendeeIds.filter(x => x !== cid) : [...f.attendeeIds, cid],
                  }))
                  return (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('attendees_req')}</label>
                    {/* Searchable multi-select dropdown: closed shows the selection summary;
                        open reveals a search box + a scrollable checkbox list. */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setAttendeeOpen(o => !o)}
                        className="w-full flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-left hover:border-blue-400"
                      >
                        <span className={`truncate ${milestoneForm.attendeeIds.length ? 'text-gray-800' : 'text-gray-400'}`}>
                          {milestoneForm.attendeeIds.length === 0
                            ? 'Select consultants…'
                            : milestoneForm.attendeeIds.length <= 2
                              ? selectedNames.join(', ')
                              : `${milestoneForm.attendeeIds.length} consultants selected`}
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${attendeeOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>

                      {attendeeOpen && (
                        <div className="mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              autoFocus
                              value={attendeeSearch}
                              onChange={e => setAttendeeSearch(e.target.value)}
                              placeholder={t('ph_search_code')}
                              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                            {consultants.length === 0 && (
                              <p className="px-3 py-2 text-xs text-gray-400">{t('no_active_consultants')}</p>
                            )}
                            {consultants.length > 0 && filtered.length === 0 && (
                              <p className="px-3 py-2 text-xs text-gray-400">No consultants match “{attendeeSearch}”.</p>
                            )}
                            {filtered.map(c => {
                              const checked = milestoneForm.attendeeIds.includes(c.id)
                              return (
                                <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                                  <input type="checkbox" checked={checked} onChange={() => toggle(c.id, checked)} />
                                  <span className="flex-1 min-w-0 text-gray-800 truncate">
                                    {c.full_name}{c.consultant_code ? ` · ${c.consultant_code}` : ''}
                                  </span>
                                </label>
                              )
                            })}
                          </div>
                          <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-100 bg-gray-50">
                            <span className="text-[11px] text-gray-500">{milestoneForm.attendeeIds.length} selected</span>
                            <button type="button" onClick={() => { setAttendeeOpen(false); setAttendeeSearch('') }} className="text-[11px] font-medium text-blue-600 hover:text-blue-800">{t('done')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Each attendee is <strong>offered</strong> this milestone right away and notified. They confirm, then you approve to issue their contract — same as a normal offer.
                    </p>
                  </div>
                  )
                })()}
              </div>

              {/* Brief file upload — optional (single-consultant milestone only) */}
              {!milestoneForm.isWorkshop && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Brief / Instructions File <span className="text-gray-400 font-normal">(optional — PDF, DOCX, etc.)</span>
                </label>
                {milestoneForm.briefFile ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700">
                    <span>📎 {milestoneForm.briefFile.name}</span>
                    <button type="button" onClick={() => setMilestoneForm({ ...milestoneForm, briefFile: null })} className="text-red-500 hover:text-red-700 ml-2">✕ Remove</button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 text-xs text-gray-500">
                    <span>📤 Click to attach a brief or instructions document for the consultant</span>
                    <input
                      type="file"
                      accept=".pdf,.docx,.doc,.xlsx,.pptx,.txt"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && setMilestoneForm({ ...milestoneForm, briefFile: e.target.files[0] })}
                    />
                  </label>
                )}
              </div>
              )}

              <div className="flex gap-3 justify-end pt-2 border-t">
                <button type="button" onClick={() => { setShowAddMilestoneModal(false); setAttendeeOpen(false); setAttendeeSearch('') }} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">{tc('actions.cancel')}</button>
                <button type="submit" disabled={milestoneSaving} className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                  {milestoneSaving ? (milestoneForm.isWorkshop ? 'Offering…' : 'Adding…') : (milestoneForm.isWorkshop ? `Offer to ${milestoneForm.attendeeIds.length || ''} Consultant${milestoneForm.attendeeIds.length === 1 ? '' : 's'}` : 'Add Milestone')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Offer-to-more-consultants Modal (reuses an existing milestone) ── */}
      {offerMoreFor && (() => {
        const q = offerMoreSearch.trim().toLowerCase()
        const filtered = consultants.filter(c =>
          !q || (c.full_name || '').toLowerCase().includes(q) || (c.consultant_code || '').toLowerCase().includes(q)
        )
        const toggle = (cid, checked) => setOfferMoreIds(prev =>
          checked ? prev.filter(x => x !== cid) : [...prev, cid]
        )
        return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-gray-900">{t('offer_more')}</h2>
            <p className="text-sm text-gray-600">
              Give the same milestone — <strong>{offerMoreFor.title}</strong>
              {(offerMoreFor.payment_amount ?? offerMoreFor.paymentAmount) > 0 && <> (${Number(offerMoreFor.payment_amount ?? offerMoreFor.paymentAmount).toLocaleString()})</>}
              {(offerMoreFor.due_date || offerMoreFor.dueDate) && <> · due {formatDate(offerMoreFor.due_date || offerMoreFor.dueDate)}</>}
              {' '}— to more consultants. Each gets their <strong>own copy</strong> and submits their own deliverables &amp; bills.
            </p>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('consultants_req')}</label>
              {/* Collapsed dropdown → opens a search box + checkbox list, same pattern as the
                  workshop attendee picker. Keeps the modal compact so the buttons stay reachable. */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOfferMoreOpen(o => !o)}
                  className="w-full flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-left hover:border-indigo-400"
                >
                  <span className={`truncate ${offerMoreIds.length ? 'text-gray-800' : 'text-gray-400'}`}>
                    {offerMoreIds.length === 0
                      ? 'Select consultants…'
                      : offerMoreIds.length <= 2
                        ? consultants.filter(c => offerMoreIds.includes(c.id)).map(c => c.full_name).join(', ')
                        : `${offerMoreIds.length} consultants selected`}
                  </span>
                  <svg className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${offerMoreOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>

                {offerMoreOpen && (
                  <div className="mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text" autoFocus value={offerMoreSearch}
                        onChange={e => setOfferMoreSearch(e.target.value)}
                        placeholder={t('ph_search_code')}
                        className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                      {consultants.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">{t('no_active_consultants')}</p>}
                      {consultants.length > 0 && filtered.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">No consultants match “{offerMoreSearch}”.</p>}
                      {filtered.map(c => {
                        const checked = offerMoreIds.includes(c.id)
                        return (
                          <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                            <input type="checkbox" checked={checked} onChange={() => toggle(c.id, checked)} />
                            <span className="flex-1 min-w-0 text-gray-800 truncate">{c.full_name}{c.consultant_code ? ` · ${c.consultant_code}` : ''}</span>
                          </label>
                        )
                      })}
                    </div>
                    <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-100 bg-gray-50">
                      <span className="text-[11px] text-gray-500">{offerMoreIds.length} selected</span>
                      <button type="button" onClick={() => { setOfferMoreOpen(false); setOfferMoreSearch('') }} className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800">{t('done')}</button>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">{offerMoreIds.length} selected · each is offered this milestone right away and notified.</p>
            </div>

            <div className="flex gap-3 justify-end pt-2 border-t">
              <button type="button" onClick={() => { setOfferMoreFor(null); setOfferMoreIds([]); setOfferMoreSearch(''); setOfferMoreOpen(false) }} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">{tc('actions.cancel')}</button>
              <button type="button" onClick={submitOfferMore} disabled={offerMoreSaving || offerMoreIds.length === 0} className="px-6 py-2 text-sm bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50">
                {offerMoreSaving ? 'Offering…' : `Offer to ${offerMoreIds.length || ''} Consultant${offerMoreIds.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
        )
      })()}

      {/* ── Add Task Modal ─────────────────────────────────────────────── */}
      {showAddTaskModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <form onSubmit={submitTask} className="p-6 space-y-4">
              <h2 className="text-xl font-bold text-gray-900">{t('add_new_task')}</h2>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('title_req')}</label>
                <input type="text" required value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder={t('ph_task_example')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.description')}</label>
                <textarea rows={3} value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.status')}</label>
                  <select value={taskForm.status} onChange={e => setTaskForm({ ...taskForm, status: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {['Not Started', 'In Progress', 'Completed', 'On Hold'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.priority')}</label>
                  <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {['Low', 'Medium', 'High'].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.due_date')}</label>
                  <input type="date" value={taskForm.due_date} onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('budget_dollar')}</label>
                  <input type="number" min="0" step="0.01" value={taskForm.budget} onChange={e => setTaskForm({ ...taskForm, budget: e.target.value })} placeholder="0" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-4 border-t">
                <button type="button" onClick={() => setShowAddTaskModal(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">{tc('actions.cancel')}</button>
                <button type="submit" disabled={taskSaving} className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                  {taskSaving ? 'Creating...' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectDetails
